(() => {
  if (window.ORJNProductGroupsV1) return;
  window.ORJNProductGroupsV1 = true;

  // ---- Size vocabulary -------------------------------------------------------------------
  // Shoes are numeric: 44, 44.5, plus the adidas alias twins 44 1/3 / 44 2/3 (docs 04 alias map).
  // Clothing + accessories added 2026-09-08. ADDITIVE BY CONSTRUCTION: every shoe value still matches
  // the first branch, and no product tag can collide — all 11,185 products carry 101 distinct tags and
  // every one is already a shoe size, so shoe grids behave exactly as before.
  // Pattern-based rather than an enumerated list on purpose: hat sizes match every eighth without the
  // vocabulary being frozen, so an unanticipated "7 5/8" filters correctly instead of silently vanishing.
  // Case-insensitive because supplier sizes are scraped page text with no case canonicalisation on write.
  // NOTE: no /g flag — these are used with .test(), which is stateful (lastIndex) on a global regex.
  const ORJN_SIZE_LIKE = /^(?:\d{1,2}(?:\.5)?(?: ?[12]\/3)?|\d{1,2} \d\/\d|\d{1,2}\/\d{1,2}|(?:XS|S|M|L|XL|XXL|[3-5]XL)T?|One Size)$/i;
  const ORJN_LETTER_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];
  // Sort key [family, primary, secondary]. Families stop unlike scales interleaving — an L must never
  // sort between 42 and 43. A shoe grid holds only numeric sizes, so everything lands in family 3 and
  // this reduces to the plain numeric sort it replaces.
  const orjnSizeRank = (raw) => {
    const s = String(raw == null ? '' : raw).trim();
    const u = s.toUpperCase(); // letter sizes compare case-insensitively, like the test above
    if (u === 'ONE SIZE') return [5, 0, 0];
    const tall = u.match(/^(XS|S|M|L|XL|XXL|[3-5]XL)T$/);
    if (tall) { const i = ORJN_LETTER_ORDER.indexOf(tall[1]); return [2, i < 0 ? 99 : i, 0]; }
    const letter = ORJN_LETTER_ORDER.indexOf(u);
    if (letter >= 0) return [1, letter, 0];
    const wi = s.match(/^(\d{1,2})\/(\d{1,2})$/);            // waist/inseam 32/32
    if (wi) return [4, parseInt(wi[1], 10), parseInt(wi[2], 10)];
    const frac = s.match(/^(\d{1,2})(?: (\d)\/(\d))?$/);     // 44, 44 1/3, hat 7 1/8
    if (frac) {
      const den = frac[3] ? parseInt(frac[3], 10) : 0;        // guard: never divide by zero
      return [3, parseInt(frac[1], 10) + (den ? parseInt(frac[2], 10) / den : 0), 0];
    }
    const n = parseFloat(s);                                  // 44.5
    if (!isNaN(n)) return [3, n, 0];
    return [6, 0, 0];
  };
  const orjnSizeCmp = (a, b) => {
    const x = orjnSizeRank(a), y = orjnSizeRank(b);
    return (x[0] - y[0]) || (x[1] - y[1]) || (x[2] - y[2]) || String(a).localeCompare(String(b));
  };

  const TEST_FLAG = 'orjn_grouped';
  const TYPE_PARAMS = [
    'filter.p.m.custom.lifestyle_type',
    'filter.p.m.custom.basketball_type',
    'filter.p.m.custom.running_type',
    'filter.p.m.custom.training_type',
    'filter.p.m.custom.tennis_type',
    // Football shoes are intentionally NOT grouped (owner request) — with football_cleats absent here,
    // football products fall through to the individual-card path in every grid (collection/search/etc.).
    // Tennis IS wired for grouping (tennis_type); until that metafield is populated on tennis products,
    // they simply render as individual cards, then group automatically once values exist.
  ];
  // Broad-type collection each group param lives in — used on the PRODUCT page to fetch that model's
  // colorways from the collection's blank feed and match them in the browser (the storefront metafield
  // filter is not enabled, so a filtered feed returns the whole catalog — see mountProduct).
  const TYPE_COLLECTION = {
    'filter.p.m.custom.lifestyle_type': 'lifestyle',
    'filter.p.m.custom.basketball_type': 'basketball',
    'filter.p.m.custom.running_type': 'running',
    'filter.p.m.custom.training_type': 'training',
    'filter.p.m.custom.tennis_type': 'tennis',
  };
  const GRID_PAGE_SIZE = 24;
  const BATCH_SIZE = 20;
  const requestCache = new Map();
  const productJsonCache = new Map();
  const fetchQueue = [];
  let activeFetches = 0;
  // Feed pages are tiny (~20 KB gzipped each) and load-time is bound by round-trips, not bytes: the
  // catalog must be fully loaded before the grid can render, so N pages / concurrency = wave count. Each
  // feed page is ~1s of Shopify server-render, so fewer waves = a big cold-load win. Raised 8 → 20 so the
  // biggest collections load in ~1-2 waves instead of 4 (lifestyle 28 pages → 2 waves; ~6.8s → ~2.5-3.5s).
  // Verified safe: 20-28-wide bursts to the blank-data feed returned 0 × 429 (HTTP/3 multiplexes, so no
  // browser connection cap), and queuedFetch still backs off + retries on any stray 429. Each shopper's
  // browser makes one burst per collection visit; Shopify's per-IP storefront GET limit tolerates it.
  const MAX_CONCURRENT_FETCHES = 20;
  // Session cache TTL for feed pages. Kept long enough to cover a browsing session so navigating back to
  // a collection (or across grouped pages) is instant; sessionStorage is per-tab and clears on tab close.
  const BLANK_CACHE_TTL = 10 * 60 * 1000;

  // Latest Drops (the new-in collection) IS grouped like everywhere else, but its group cards are ordered
  // NEWEST-first ("latest drops first") instead of the default alphabetical order — see the typeEntries
  // sort in mountGrid. (Football is excluded from grouping separately, via TYPE_PARAMS above.)
  const IS_LATEST_DROPS = () => /\/collections\/new-in(?:\/|$)/.test(location.pathname);
  const enabled = () => new URLSearchParams(location.search).get(TEST_FLAG) !== '0';
  const esc = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[char]);

  // A shopper-facing size can represent more than one brand-specific variant label: for example,
  // filter size 44.5 represents both Nike/Jordan 44.5 and Adidas 44 2/3. Carry every alias to the
  // PDP only when exactly one displayed size is active, so the PDP can select the label it actually has.
  const filteredSizeAliases = () => {
    const params = new URLSearchParams(location.search);
    const carried = [
      ...(params.get('orjn_filter_sizes') || '').split('|'),
      ...params.getAll('orjn_filter_size'),
    ].map((value) => value.trim()).filter(Boolean);
    if (carried.length) return [...new Set(carried)];
    if (location.pathname.startsWith('/products/')) return [];

    // Our client-side filter writes one comma-joined tag param ("44,44 1/3") so Shopify will not drop
    // duplicate values on a later reload. Native filters can still emit repeated params. Read both forms.
    const applied = new Set(params.getAll('filter.p.tag')
      .flatMap((value) => String(value).split(','))
      .map((value) => value.trim()).filter(Boolean));
    if (!applied.size) return [];
    const groups = new Map();
    document.querySelectorAll('input[data-orjn-size-tags], input[data-orjn-size-tag], input[name="orjn_size_tag"]').forEach((input) => {
      const raw = input.dataset.orjnSizeTags || input.dataset.orjnSizeTag || input.value || '';
      const aliases = raw.split('|').map((value) => value.trim()).filter(Boolean);
      if (!aliases.some((value) => applied.has(value))) return;
      const label = (input.dataset.label || input.value || aliases[0] || '').trim();
      if (label && !groups.has(label)) groups.set(label, aliases);
    });
    if (groups.size === 1) return [...new Set(groups.values().next().value)];

    // Generated size chips are buttons, not native filter inputs. If no native DOM mapping is available,
    // rebuild the alias groups from the URL itself. Carry one displayed size (both aliases) to the PDP;
    // never guess when two genuinely different displayed sizes are selected.
    const aliasGroups = new Map();
    applied.forEach((raw) => {
      if (!ORJN_SIZE_LIKE.test(raw)) return;
      const m = raw.match(/^(\d{1,2})(\.5)?( ?([12])\/3)?$/);
      if (!m) { aliasGroups.set(raw, [raw]); return; } // clothing sizes have no alias twin
      const whole = parseInt(m[1], 10);
      const aliases = (m[2] === '.5' || m[4] === '2')
        ? [whole + '.5', whole + ' 2/3']
        : ['' + whole, whole + ' 1/3'];
      aliasGroups.set(aliases.join('|'), aliases);
    });
    return aliasGroups.size === 1 ? aliasGroups.values().next().value : [];
  };

  const appendFilteredSize = (url) => {
    url.searchParams.delete('orjn_filter_size');
    url.searchParams.delete('orjn_filter_sizes');
    const aliases = filteredSizeAliases();
    if (aliases.length) url.searchParams.set('orjn_filter_sizes', aliases.join('|'));
    return url;
  };

  const productHref = (rawUrl) => {
    const url = appendFilteredSize(new URL(rawUrl, location.origin));
    return `${url.pathname}${url.search}${url.hash}`;
  };

  // Display-only gender label for the card badge — mirrors snippets/product-card.liquid exactly:
  // a product tagged for both men and women (gender "Men, Women") shows "Unisex". The underlying
  // gender value and all filtering/URLs are unchanged; this only changes what the shopper sees.
  const orjnGenderLabel = (g) => {
    if (!g) return '';
    const dc = String(g).toLowerCase();
    const hasWomen = dc.includes('women');
    const hasMen = dc.replace(/women/g, '').includes('men');
    if (hasWomen && hasMen) return 'Unisex';
    if (hasWomen) return 'Women';
    if (hasMen) return 'Men';
    return g;
  };

  const batchSizes = (total) => {
    const count = Math.max(0, Number(total) || 0);
    if (!count) return [];
    if (count <= BATCH_SIZE + 4) return [count];
    const full = Math.floor(count / BATCH_SIZE);
    const remainder = count % BATCH_SIZE;
    const sizes = Array(full).fill(BATCH_SIZE);
    if (remainder >= 5) sizes.push(remainder);
    else if (remainder > 0) sizes[sizes.length - 1] += remainder;
    return sizes;
  };

  // A compare-at from one size must never be compared with a different size's price.
  // Any discounted size makes the colorway a sale item; a selected size gets its own price treatment.
  const isSaleProduct = (product) => {
    const prices = orjnParseSizePrices(product?.sizePrices);
    const compares = orjnParseSizePrices(product?.sizeCompareAt);
    const sizes = Object.keys(prices);
    if (sizes.length) return sizes.some((size) => compares[size] > prices[size]);
    return Number(product?.compareAtPrice || 0) > Number(product?.price || 0);
  };

  const productBatches = (products) => {
    const batches = [];
    let offset = 0;
    batchSizes(products.length).forEach((size) => {
      batches.push(products.slice(offset, offset + size));
      offset += size;
    });
    return batches;
  };

  const queuedFetch = (url) => new Promise((resolve, reject) => {
    const run = async () => {
      activeFetches += 1;
      try {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json, text/plain, */*' },
          });
          const text = await response.text();
          if (response.ok) {
            try { resolve(JSON.parse(text)); }
            catch { throw new Error('Shopify returned an unexpected response'); }
            return;
          }
          if (response.status !== 429 || attempt === 2) throw new Error(`Shopify returned ${response.status}`);
          const retryAfter = Number(response.headers.get('retry-after'));
          const delay = Number.isFinite(retryAfter) && retryAfter > 0
            ? Math.min(3000, retryAfter * 1000)
            : 400 * (2 ** attempt);
          await new Promise((resume) => window.setTimeout(resume, delay));
        }
      } catch (error) {
        reject(error);
      } finally {
        activeFetches -= 1;
        fetchQueue.shift()?.();
      }
    };
    if (activeFetches < MAX_CONCURRENT_FETCHES) run();
    else fetchQueue.push(run);
  });

  const fetchJson = async (url, options = {}) => {
    const key = url.toString();
    const sessionKey = `orjn-catalog-v5:${key}`;
    if (options.session) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(sessionKey) || 'null');
        if (cached && Date.now() - cached.savedAt < BLANK_CACHE_TTL) return cached.data;
        if (cached) sessionStorage.removeItem(sessionKey);
      } catch { sessionStorage.removeItem(sessionKey); }
    }
    if (!requestCache.has(key)) {
      // Reuse the loader's early <head> prefetch (started during HTML parse, before this deferred script ran)
      // when it matches this exact URL; if it failed, fall back to the normal retrying fetch so 429s etc. are
      // still handled. The loader prefetches the WHOLE feed (every page) into window.__orjnFeedPrefetch, so
      // every loadCatalog page is a reuse, not a duplicate round-trip. We also REGISTER our own fetch back
      // into that map, so whichever side (loader or engine) starts a page first, the other reuses it — no
      // page is ever fetched twice, in any order. (window.__orjnFeed1 is the older page-1-only fallback.)
      const prefetch = (window.__orjnFeedPrefetch = window.__orjnFeedPrefetch || {});
      const pre = prefetch[key]
        || ((window.__orjnFeed1 && window.__orjnFeed1.key === key) ? window.__orjnFeed1.promise : null);
      const source = pre ? pre.catch(() => queuedFetch(key)) : queuedFetch(key);
      prefetch[key] = source;
      requestCache.set(key, source.then((data) => {
        if (options.session) {
          try { sessionStorage.setItem(sessionKey, JSON.stringify({ savedAt: Date.now(), data })); }
          catch { /* Browser storage may be disabled or full; memory caching still works. */ }
        }
        return data;
      }).catch((error) => {
          requestCache.delete(key);
          throw error;
        }));
    }
    return requestCache.get(key);
  };

  const collectTypes = () => {
    const found = new Map();
    document.querySelectorAll('input[data-orjn-filter-count]').forEach((input) => {
      if (!TYPE_PARAMS.includes(input.name) || input.disabled) return;
      const count = Number(input.dataset.orjnFilterCount || 0);
      if (count <= 0 && !input.checked) return;
      const key = `${input.name}\u0000${input.value}`;
      const existing = found.get(key);
      if (!existing || input.checked) {
        found.set(key, {
          param: input.name,
          value: input.value,
          label: input.dataset.label || input.value,
          count,
          active: input.checked,
        });
      }
    });
    const values = Array.from(found.values());
    const active = values.filter((item) => item.active);
    const visible = active.length ? active : values;
    return visible.sort((a, b) => {
      const field = TYPE_PARAMS.indexOf(a.param) - TYPE_PARAMS.indexOf(b.param);
      return field || a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  const finishPending = () => {
    document.documentElement.classList.remove('orjn-groups-pending');
    document.querySelectorAll('.orjn-groups-skeleton').forEach((element) => element.remove());
  };

  const dataUrl = (type, page, pathname = location.pathname) => {
    const url = new URL(location.href);
    url.pathname = pathname;
    url.searchParams.delete('page');
    url.searchParams.delete('orjn_group_page');
    TYPE_PARAMS.forEach((name) => url.searchParams.delete(name));
    url.searchParams.set(type.param, type.value);
    url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('view', 'orjn-group-data');
    url.searchParams.set('page', String(page));
    if (url.pathname === '/search') url.searchParams.set('type', 'product');
    return url;
  };

  const blankDataUrl = (page) => {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('orjn_group_page');
    // Always fetch the collection/search's FULL catalog. Filters are applied to this cached catalog in the
    // browser, so changing a filter is an instant Lifestyle-style re-render instead of a Shopify page reload.
    [...url.searchParams.keys()].forEach((key) => { if (key.startsWith('filter.')) url.searchParams.delete(key); });
    // Preserve Shopify's source order for Best selling: a group's strongest-ranked colorway determines
    // the group's position. Other modes are re-sorted locally and keep a canonical feed/cache key.
    if (url.searchParams.get('sort_by') !== 'best-selling') url.searchParams.delete('sort_by');
    url.searchParams.set('view', 'orjn-blank-data');
    url.searchParams.set('page', String(page));
    if (url.pathname === '/search') url.searchParams.set('type', 'product');
    return url;
  };

  const dedupeProducts = (chunks) => {
    const seen = new Set();
    return chunks.flatMap((chunk) => Array.isArray(chunk?.products) ? chunk.products : []).filter((product) => {
      const key = String(product.id);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Load every page of a feed. The grid can't render until the whole collection is loaded (grouping needs
  // all colorways) and each page is a separate Shopify request, so wall-clock ≈ (pages / concurrency)
  // round-trips. To avoid paying a full round-trip for page 1 ALONE before we learn the page count, we
  // fire page 1 together with a speculative first wave (up to MAX_CONCURRENT_FETCHES). Pages past the real
  // count resolve empty (guarded) and are dropped; results dedupe by id, so the bounded over-fetch on a
  // small collection is harmless. Speculative pages are session-cached too, so a warm revisit stays all
  // cache hits (instant).
  // MEASURED 2026-09-01, do not "optimise" this to a small burst: capping the speculative wave at 7 does
  // remove the wasted requests on small feeds (basketball 20→8, search 21→9), but it costs the BIGGEST
  // collection dearly — lifestyle's first card went ~7.3s → ~12.3s on the same theme, because the 20 pages
  // beyond the burst can only start once page 1 returns, instead of already being in flight. The wasted
  // pages are cheap for the shopper; the extra round-trip is not. See doc 12 "speculative feed burst".
  const loadAllPages = async (urlFor, pageHint = 0) => {
    const hintedPages = Math.max(0, Math.floor(Number(pageHint) || 0));
    if (hintedPages > 0) {
      // The loader gets this count from Liquid and starts these exact pages during HTML parsing. Reuse that
      // wave instead of blindly requesting pages 1..20 on every small/filtered collection. Page 1's JSON
      // count remains authoritative, so an imperfect search/count hint can never truncate the catalog.
      const hinted = Array.from({ length: hintedPages }, (_, index) =>
        fetchJson(urlFor(index + 1), { session: true }));
      const first = await hinted[0];
      const pages = Math.max(1, Number(first.pages) || 1);
      const used = hinted.slice(1, pages);
      const tail = pages > hintedPages
        ? Array.from({ length: pages - hintedPages }, (_, index) =>
            fetchJson(urlFor(hintedPages + index + 1), { session: true }).catch(() => ({ products: [] })))
        : [];
      const rest = await Promise.all([...used, ...tail]);
      return dedupeProducts([first, ...rest]);
    }
    const firstPromise = fetchJson(urlFor(1), { session: true });
    const speculative = Array.from({ length: MAX_CONCURRENT_FETCHES - 1 }, (_, index) =>
      fetchJson(urlFor(index + 2), { session: true }).catch(() => ({ products: [] })));
    const first = await firstPromise;
    const pages = Math.max(1, Number(first.pages) || 1);
    if (pages === 1) return dedupeProducts([first]);
    const used = speculative.slice(0, pages - 1);
    const tail = pages > MAX_CONCURRENT_FETCHES
      ? Array.from({ length: pages - MAX_CONCURRENT_FETCHES }, (_, index) =>
          fetchJson(urlFor(MAX_CONCURRENT_FETCHES + index + 1), { session: true }).catch(() => ({ products: [] })))
      : [];
    const rest = await Promise.all([...used, ...tail]);
    return dedupeProducts([first, ...rest]);
  };

  const loadCatalog = async () => {
    const products = await loadAllPages(blankDataUrl, window.__orjnFeedPageCount);
    if (!IS_LATEST_DROPS()) return products;
    // Shopify's New In storefront shows the newest 240 shoes (10 × 24). Its custom
    // blank-data feed exposes the whole collection, so scope *before* facets/grouping.
    // Preserve source order afterwards: best-selling still ranks this recent cohort.
    const newest = new Set([...products]
      .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0))
      .slice(0, 240).map((product) => String(product.id)));
    return products.filter((product) => newest.has(String(product.id)));
  };
  const loadTypeCatalog = (type) => loadAllPages((page) => dataUrl(type, page));

  const groupsForType = (type, products) => {
    const groups = [];
    ['normal', 'sale'].forEach((bucket) => {
      const matching = products
        .filter((product) => isSaleProduct(product) === (bucket === 'sale'))
        .sort(orjnByPriceAsc);
      productBatches(matching).forEach((batch, index) => groups.push({
        type,
        size: batch.length,
        page: index + 1,
        sale: bucket === 'sale',
        products: batch,
      }));
    });
    return groups;
  };

  const productUrl = (product, group) => {
    const url = appendFilteredSize(new URL(product.url, location.origin));
    url.searchParams.set('orjn_group_param', group.type.param);
    url.searchParams.set('orjn_group_value', group.type.value);
    url.searchParams.set('orjn_group_batch', String(group.page));
    url.searchParams.set('orjn_group_sale', group.sale ? '1' : '0');
    return `${url.pathname}${url.search}`;
  };

  const loadGroup = async (group) => {
    if (Array.isArray(group.products)) return group.products;
    const first = await fetchJson(dataUrl(group.type, group.page), { session: true });
    let products = Array.isArray(first.products) ? first.products : [];
    if (group.size > BATCH_SIZE) {
      const tail = await fetchJson(dataUrl(group.type, group.page + 1), { session: true });
      products = products.concat(Array.isArray(tail.products) ? tail.products : []);
    }
    return orjnFilterProducts(products).sort(orjnByPriceAsc).slice(0, group.size);
  };

  const orjnLoadThumbImage = (thumb) => {
    const image = thumb?.querySelector('img[data-src]');
    if (!image) return;
    image.src = image.dataset.src;
    image.removeAttribute('data-src');
  };

  const setSelectedProduct = async (card, group, products, index, scrollThumb = true) => {
    const selected = products[index];
    if (!selected) return;
    card.dataset.activeIndex = String(index);
    const href = productUrl(selected, group);
    const imageLink = card.querySelector('[data-orjn-group-media]');
    const image = card.querySelector('[data-orjn-group-image]');
    const title = card.querySelector('[data-orjn-group-selected]');
    const price = card.querySelector('[data-orjn-group-price]');
    const badge = card.querySelector('[data-orjn-group-badge]');
    const position = card.querySelector('[data-orjn-group-position]');
    const progress = card.querySelector('[data-orjn-group-progress]');
    if (imageLink) imageLink.href = href;
    if (title) { title.textContent = selected.title; title.href = href; }
    // Point the card's quick-add at the newly-selected colorway; reset its size panel so
    // reopening loads the right sizes (re-hydrate live if it is already open).
    card.dataset.selectedHandle = selected.handle || '';
    const orjnQa = card.querySelector('[data-orjn-group-qa]');
    if (orjnQa) {
      const qaView = orjnQa.querySelector('[data-orjn-group-qa-view]');
      if (qaView) qaView.href = href;
      if (orjnQa.dataset.orjnGroupQaHandle !== (selected.handle || '')) {
        delete orjnQa.dataset.orjnGroupQaState;
        const qaSizes = orjnQa.querySelector('[data-orjn-group-qa-sizes]');
        if (qaSizes) qaSizes.innerHTML = '';
        const qaLabel = orjnQa.querySelector('[data-orjn-group-qa-label]');
        if (qaLabel) qaLabel.textContent = 'Loading sizes…';
        if (orjnQa.classList.contains('is-open')) hydrateGroupQuickAdd(orjnQa);
      }
    }
    if (price) price.innerHTML = orjnPriceHTML(selected, true);
    if (badge) badge.textContent = selected.ageRange || orjnGenderLabel(selected.gender);
    if (position) position.textContent = `${index + 1} / ${products.length}`;
    if (progress) progress.style.width = `${((index + 1) / products.length) * 100}%`;
    card.querySelectorAll('[data-orjn-group-thumb]').forEach((thumb, thumbIndex) => {
      thumb.classList.toggle('is-active', thumbIndex === index);
      thumb.setAttribute('aria-pressed', thumbIndex === index ? 'true' : 'false');
    });
    if (scrollThumb) {
      const thumb = card.querySelector(`[data-orjn-group-thumb="${index}"]`);
      const rail = card.querySelector('[data-orjn-group-thumbs]');
      if (thumb && rail) {
        orjnLoadThumbImage(thumb);
        const left = thumb.offsetLeft - ((rail.clientWidth - thumb.offsetWidth) / 2);
        rail.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }
    if (image) {
      let source = selected.image || selected.thumb || '';
      // Above-fold cards: their MAIN (selected colour) image is high-priority so it downloads first — ahead
      // of the lazy colour-selector thumbnails and below-fold cards. This shows the customer the primary
      // product image of each visible card fastest, then the other colours' thumbnails fill in.
      const orjnGi = Number(card.dataset.groupIndex) || 0;
      const cardRect = card.getBoundingClientRect();
      const cardIsVisible = cardRect.bottom > 0 && cardRect.top < window.innerHeight;
      const initialPriorityCards = window.matchMedia('(max-width:749px)').matches ? 4 : 6;
      const priorityMain = scrollThumb || orjnGi < initialPriorityCards || cardIsVisible;
      // Deferred cards are hydrated by IntersectionObserver. When the shopper jumps/scrolls directly to a
      // lower card, its main image is now foreground work too; without this visibility check card 11+ kept
      // the browser's default priority and could wait behind thumbnail/background traffic on slow Wi-Fi.
      if (priorityMain) {
        image.loading = 'eager';
        image.fetchPriority = 'high';
      }
      // Right-size the card image to how big it ACTUALLY shows (× the screen's pixel density) instead of the
      // fixed 720px it always requested — ~4× too big on a ~170px phone card / ~257px laptop card. Image bytes
      // scale with area, so this roughly halves them on slow wifi with no visible quality drop. Respects both
      // phone and laptop (uses each card's real rendered width), never upscales (capped at the 720 source),
      // and is snapped to 60px steps so many shoppers hit the same cached CDN size. Collection cards only —
      // the product page renders its own images, so it's untouched.
      if (source && /[?&]width=\d+/.test(source)) {
        const box = image.getBoundingClientRect().width || card.getBoundingClientRect().width || 270;
        const need = Math.min(720, Math.max(240, Math.ceil((box * (window.devicePixelRatio || 1)) / 60) * 60));
        source = source.replace(/([?&]width=)\d+/, '$1' + need);
      }
      const selectionToken = String((Number(card.dataset.imageToken) || 0) + 1);
      card.dataset.imageToken = selectionToken;
      card.classList.add('is-image-loading');
      image.style.visibility = 'hidden';
      image.style.opacity = '0';
      if (!source) {
        image.style.visibility = 'visible';
        card.classList.remove('is-image-loading');
        return;
      }

      // A shopper-triggered colour switch is foreground work. Promote the same URL if it is
      // still waiting in (or already loading through) the low-priority colourway queue, then
      // issue a high-priority request directly so a card near the bottom of the grid never
      // waits behind background preloads from earlier cards.
      const resumeColorPreloads = scrollThumb ? orjnPromoteColorway(source) : null;
      const preload = new Image();
      preload.decoding = 'async';
      if (priorityMain) preload.fetchPriority = 'high';
      let loaded = false;
      try {
        loaded = await new Promise((resolve) => {
          let timer;
          const done = (success) => {
            window.clearTimeout(timer);
            preload.onload = null;
            preload.onerror = null;
            resolve(success);
          };
          preload.onload = () => done(true);
          preload.onerror = () => done(false);
          timer = window.setTimeout(() => done(false), 8000);
          preload.src = source;
        });
        if (loaded && preload.decode) {
          try { await preload.decode(); } catch { /* The load event is still a safe fallback. */ }
        }
      } finally {
        if (resumeColorPreloads) resumeColorPreloads();
      }
      if (card.dataset.imageToken !== selectionToken) return;

      image.src = source;
      image.alt = selected.imageAlt || selected.title || group.type.label;
      if (loaded && image.decode) {
        try { await image.decode(); } catch { /* Keep the loaded resource as fallback. */ }
      }
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (card.dataset.imageToken === selectionToken) {
        image.style.visibility = 'visible';
        image.style.opacity = '1';
        card.classList.remove('is-image-loading');
      }
    }
  };

  // Responsive card-image width (must match setSelectedProduct so preloaded URLs are cache hits on switch).
  const orjnCardImgWidth = (card) => {
    const img = card.querySelector('.orjn-group-card__image');
    const box = (img && img.getBoundingClientRect().width) || card.getBoundingClientRect().width || 270;
    return Math.min(720, Math.max(240, Math.ceil((box * (window.devicePixelRatio || 1)) / 60) * 60));
  };

  // Background colour warming has its own deliberately small lane. Creating every Image at once
  // lets dozens of low-priority requests occupy the browser scheduler before a later card is used.
  // Keeping only two background images in flight preserves connection capacity for an explicitly
  // selected colour, which always bypasses this queue through setSelectedProduct above.
  const ORJN_COLOR_PRELOAD_CONCURRENCY = 2;
  const orjnColorPreloadQueue = [];
  const orjnColorPreloadJobs = new Map();
  let orjnColorPreloadsActive = 0;
  let orjnColorPreloadsPaused = 0;

  const orjnDrainColorPreloads = () => {
    if (orjnColorPreloadsPaused) return;
    while (orjnColorPreloadsActive < ORJN_COLOR_PRELOAD_CONCURRENCY && orjnColorPreloadQueue.length) {
      const job = orjnColorPreloadQueue.shift();
      if (!job || job.status !== 'queued') continue;
      job.status = 'loading';
      orjnColorPreloadsActive += 1;
      const im = new Image();
      job.image = im;
      im.decoding = 'async';
      try { im.fetchPriority = 'low'; } catch (e) { /* older browsers ignore */ }
      const done = (status) => {
        if (job.status === 'loading') job.status = status;
        job.image = null;
        orjnColorPreloadsActive = Math.max(0, orjnColorPreloadsActive - 1);
        orjnDrainColorPreloads();
      };
      im.onload = () => done('loaded');
      im.onerror = () => done('failed');
      im.src = job.src;
    }
  };

  const orjnQueueColorPreload = (src) => {
    if (!src || orjnColorPreloadJobs.has(src)) return;
    const job = { src, status: 'queued', image: null };
    orjnColorPreloadJobs.set(src, job);
    orjnColorPreloadQueue.push(job);
    orjnDrainColorPreloads();
  };

  const orjnPromoteColorway = (src) => {
    const job = orjnColorPreloadJobs.get(src);
    if (job?.status === 'queued') {
      job.status = 'promoted';
    } else if (job?.status === 'loading' && job.image) {
      try { job.image.fetchPriority = 'high'; } catch (e) { /* older browsers ignore */ }
    }
    // Priority hints cannot interrupt two low-priority transfers that already occupy the connection pool.
    // Pause those unrelated warmups while the shopper's selected colour loads, then resume them afterwards.
    orjnColorPreloadsPaused += 1;
    orjnColorPreloadJobs.forEach((other) => {
      if (other === job || other.status !== 'loading' || !other.image) return;
      const image = other.image;
      image.onload = null;
      image.onerror = null;
      other.image = null;
      other.status = 'queued';
      orjnColorPreloadsActive = Math.max(0, orjnColorPreloadsActive - 1);
      orjnColorPreloadQueue.push(other);
      try { image.src = ''; } catch (e) { /* best-effort abort */ }
    });
    let resumed = false;
    return () => {
      if (resumed) return;
      resumed = true;
      orjnColorPreloadsPaused = Math.max(0, orjnColorPreloadsPaused - 1);
      orjnDrainColorPreloads();
    };
  };

  // "Loading list": once a shopper engages a card (hover on desktop / first tap on mobile), pre-fetch ALL
  // its colourway images into the browser cache at LOW priority. Switching colours is then instant (no wait,
  // no shimmer — a cached image shows immediately). Runs once per card and only for cards actually engaged.
  // The shared two-wide queue prevents earlier cards from flooding the connection; a colour the shopper
  // explicitly selects is promoted and bypasses the queue immediately.
  const orjnPreloadColorways = (card, products) => {
    if (card._orjnColorsPreloaded || !products || products.length < 2) return;
    card._orjnColorsPreloaded = true;
    const w = orjnCardImgWidth(card);
    products.forEach((product, index) => {
      if (index === Number(card.dataset.activeIndex || 0)) return; // the visible colour is already loading/loaded
      const src = (product && product.image || '').replace(/([?&]width=)\d+/, '$1' + w);
      orjnQueueColorPreload(src);
    });
  };

  const hydrateCard = async (card, group) => {
    if (card.dataset.loaded === 'true') return;
    card.dataset.loaded = 'loading';
    try {
      const products = await loadGroup(group);
      if (!products.length) throw new Error('No matching published products');
      card._orjnProducts = products;
      const thumbs = card.querySelector('[data-orjn-group-thumbs]');
      thumbs.innerHTML = products.map((product, index) => `
        <button class="orjn-group-card__thumb${index === 0 ? ' is-active' : ''}" type="button"
          data-orjn-group-thumb="${index}" aria-label="${esc(product.title)}" aria-pressed="${index === 0}">
          <img ${index < 3 ? 'src' : 'data-src'}="${esc(product.thumb || product.image)}" alt="" loading="lazy" fetchpriority="low" width="64" height="64">
        </button>
      `).join('');
      // A grouped card can contain 20+ colours. Loading every horizontally-hidden thumbnail as soon as the
      // card enters the vertical viewport flooded slow connections and starved the next visible MAIN image.
      // Start only the three visible rail thumbnails; reveal the rest just before they enter the rail.
      if ('IntersectionObserver' in window) {
        const thumbObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          orjnLoadThumbImage(entry.target);
          thumbObserver.unobserve(entry.target);
        }), { root: thumbs, rootMargin: '0px 120px' });
        thumbs.querySelectorAll('[data-orjn-group-thumb]').forEach((thumb) => thumbObserver.observe(thumb));
        card._orjnThumbObserver = thumbObserver;
      } else {
        thumbs.querySelectorAll('[data-orjn-group-thumb]').forEach(orjnLoadThumbImage);
      }
      // Warm the whole colour list into cache as soon as the shopper engages this card (hover on desktop,
      // first touch on mobile) — so every colour switch after that is instant.
      card.addEventListener('pointerenter', () => orjnPreloadColorways(card, products), { once: true });
      thumbs.addEventListener('click', (event) => {
        const button = event.target.closest('[data-orjn-group-thumb]');
        if (!button) return;
        setSelectedProduct(card, group, products, Number(button.dataset.orjnGroupThumb));
        orjnPreloadColorways(card, products);
      });
      card.querySelector('[data-orjn-group-prev]').addEventListener('click', () => {
        const next = (Number(card.dataset.activeIndex || 0) - 1 + products.length) % products.length;
        setSelectedProduct(card, group, products, next);
        orjnPreloadColorways(card, products);
      });
      card.querySelector('[data-orjn-group-next]').addEventListener('click', () => {
        const next = (Number(card.dataset.activeIndex || 0) + 1) % products.length;
        setSelectedProduct(card, group, products, next);
        orjnPreloadColorways(card, products);
      });
      await setSelectedProduct(card, group, products, 0, false);
      card.classList.remove('is-loading');
      card.dataset.loaded = 'true';
    } catch (error) {
      card.classList.remove('is-loading');
      card.dataset.loaded = 'error';
      card.querySelector('.orjn-group-card__shell').innerHTML = `
        <div class="orjn-group-card__error">Could not load this group. <button type="button" data-orjn-group-retry>Try again</button></div>`;
      card.querySelector('[data-orjn-group-retry]')?.addEventListener('click', () => {
        card.dataset.loaded = 'false';
        location.reload();
      });
      console.error('[ORJN groups]', error);
    }
  };

  const groupKey = (group) => [
    group.type.param,
    group.type.value,
    group.sale ? 'sale' : 'normal',
    group.page,
    group.products?.[0]?.id || '',
  ].join('\u0000');

  // Quick add-to-cart on a group card (adds the currently-shown colorway). Reuses the
  // theme's shared .orjn-card-qa handler (toggle + POST /cart/add.js); we only load the
  // selected colorway's sizes lazily.
  const orjnGroupQaMarkup = () => `
        <div class="orjn-card-qa orjn-group-card__qa" data-orjn-qa data-orjn-group-qa>
          <button type="button" class="orjn-card-qa__trigger" data-orjn-qa-trigger data-orjn-group-qa-trigger aria-expanded="false" aria-label="Select size and add to cart">
            <span class="orjn-card-qa__trigger-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round"><path d="M8 10V8a4 4 0 1 1 8 0v2"></path><path d="M5 10.5h14l-1.1 8.8a1.5 1.5 0 0 1-1.49 1.32H7.59A1.5 1.5 0 0 1 6.1 19.3L5 10.5Z"></path><path d="M12 13.25v4.5"></path><path d="M9.75 15.5h4.5"></path></svg></span>
          </button>
          <div class="orjn-card-qa__panel" data-orjn-qa-panel hidden>
            <div class="orjn-card-qa__panel-head"><span class="orjn-card-qa__eyebrow">Quick add</span><span class="orjn-card-qa__label" data-orjn-group-qa-label>Loading sizes…</span></div>
            <div class="orjn-card-qa__sizes" data-orjn-group-qa-sizes></div>
            <p class="orjn-card-qa__message" data-orjn-qa-message hidden></p>
            <a class="orjn-card-qa__view-link" data-orjn-group-qa-view href="#">View full product</a>
          </div>
        </div>`;

  const cardMarkup = (group, index) => {
    const card = document.createElement('li');
    card.className = `product-grid__item orjn-group-card is-loading${group.size === 1 ? ' is-single' : ''}${group.sale ? ' is-sale' : ''}`;
    card.dataset.groupIndex = String(index);
    card.dataset.groupKey = groupKey(group);
    card.innerHTML = `
      <article class="orjn-group-card__shell">
        <a class="orjn-group-card__media" data-orjn-group-media aria-label="Open ${esc(group.type.label)}">
          <img class="orjn-group-card__image" data-orjn-group-image alt="" width="720" height="720">
          ${group.sale ? '<span class="orjn-group-card__sale-badge"><span>ON SALE</span></span>' : ''}
        </a>
        ${orjnGroupQaMarkup()}
        <div class="orjn-group-card__head">
          <h3 class="orjn-group-card__type">${esc(group.type.label)}</h3>
          <span class="orjn-group-card__count">${group.size} ${group.size === 1 ? 'COLOR' : 'COLORS'}</span>
        </div>
        <a class="orjn-group-card__selected" data-orjn-group-selected></a>
        <div class="orjn-group-card__meta">
          <span data-orjn-group-price></span><span class="orjn-group-card__badge" data-orjn-group-badge></span>
        </div>
        <div class="orjn-group-card__controls">
          <button class="orjn-group-card__arrow orjn-group-card__arrow--left" type="button" data-orjn-group-prev aria-label="Previous color">&#8592;</button>
          <div class="orjn-group-card__rail">
            <div class="orjn-group-card__thumbs" data-orjn-group-thumbs></div>
            <div class="orjn-group-card__rail-meta">
              <span class="orjn-group-card__progress"><span data-orjn-group-progress></span></span>
              <span class="orjn-group-card__position" data-orjn-group-position></span>
            </div>
          </div>
          <button class="orjn-group-card__arrow orjn-group-card__arrow--right" type="button" data-orjn-group-next aria-label="Next color">&#8594;</button>
        </div>
      </article>`;
    return card;
  };

  const individualCardMarkup = (product, visibleIndex = Number.MAX_SAFE_INTEGER) => {
    const card = document.createElement('li');
    card.className = 'product-grid__item orjn-individual-card';
    const priorityImage = visibleIndex < 5;
    const href = productHref(product.url);
    // Right-size the individual-card image (was 720px, shown at ~170-257px) to 600 — crisp on both retina
    // phone (~513px needed) and retina laptop (~514px), ~30% fewer image bytes. Static markup, so it can't
    // measure per-device like the group cards; 600 is a safe fits-both size.
    const indImage = (product.image || '').replace(/([?&]width=)\d+/, '$1600');
    card.innerHTML = `
      <article class="orjn-individual-card__shell">
        ${product.available !== false && (orjnSizeState()
          ? Boolean(orjnSizePriceInfo(product, orjnSizeState())?.compareAt)
          : isSaleProduct(product)) ? '<span class="orjn-group-card__sale-badge">ON SALE</span>' : ''}
        <a class="orjn-individual-card__media" href="${esc(href)}" aria-label="${esc(product.title)}">
          <img src="${esc(indImage)}" alt="${esc(product.imageAlt || product.title)}" loading="${priorityImage ? 'eager' : 'lazy'}"${priorityImage ? ' fetchpriority="high"' : ''} decoding="async" width="720" height="720">
        </a>
        ${product.available === false ? '' : `
          <div class="orjn-card-qa orjn-individual-card__qa" data-orjn-qa data-orjn-individual-qa data-product-handle="${esc(product.handle)}">
            <button type="button" class="orjn-card-qa__trigger" data-orjn-qa-trigger data-orjn-individual-qa-trigger aria-expanded="false" aria-label="Select size and add ${esc(product.title)} to cart">
              <span class="orjn-card-qa__trigger-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M8 10V8a4 4 0 1 1 8 0v2"></path><path d="M5 10.5h14l-1.1 8.8a1.5 1.5 0 0 1-1.49 1.32H7.59A1.5 1.5 0 0 1 6.1 19.3L5 10.5Z"></path><path d="M12 13.25v4.5"></path><path d="M9.75 15.5h4.5"></path>
                </svg>
              </span>
            </button>
            <div class="orjn-card-qa__panel" data-orjn-qa-panel hidden>
              <div class="orjn-card-qa__panel-head"><span class="orjn-card-qa__eyebrow">Quick add</span><span class="orjn-card-qa__label" data-orjn-individual-qa-label>Loading sizes…</span></div>
              <div class="orjn-card-qa__sizes" data-orjn-individual-qa-sizes></div>
              <p class="orjn-card-qa__message" data-orjn-qa-message hidden></p>
              <a class="orjn-card-qa__view-link" href="${esc(href)}">View full product</a>
            </div>
          </div>`}
        <a class="orjn-individual-card__title" href="${esc(href)}">${esc(product.title)}</a>
        <div class="orjn-individual-card__meta">
          <span>${orjnPriceHTML(product, false)}</span>
          <span class="orjn-individual-card__badge">${esc(product.ageRange || orjnGenderLabel(product.gender))}</span>
        </div>
      </article>`;
    return card;
  };

  const loadProductJson = (handle) => {
    if (!productJsonCache.has(handle)) {
      const url = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`;
      const request = async () => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (response.ok) return response.json();
          if (response.status !== 429 || attempt === 2) throw new Error(`Shopify returned ${response.status}`);
          const retryAfter = Number(response.headers.get('retry-after'));
          const delay = Number.isFinite(retryAfter) && retryAfter > 0
            ? Math.min(5000, retryAfter * 1000)
            : 800 * (2 ** attempt);
          await new Promise((resolve) => window.setTimeout(resolve, delay));
        }
        throw new Error('Shopify product data was unavailable');
      };
      productJsonCache.set(handle, request().catch((error) => {
        productJsonCache.delete(handle);
        throw error;
      }));
    }
    return productJsonCache.get(handle);
  };

  // Shared quick-add size builder — one distinct size per button, with that size's own price
  // (ORJN prices vary by size). Used by both the individual-card and group-card quick-adds.
  const orjnQaMoney = (cents) => '$' + (Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const orjnQaSizeValues = (product) => {
    const optionNames = Array.isArray(product.options) ? product.options.map((option) => typeof option === 'string' ? option : option?.name || '') : [];
    let sizeIndex = optionNames.findIndex((name) => name.toLowerCase().includes('size'));
    if (sizeIndex < 0 && optionNames.length === 1) sizeIndex = 0;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const seen = new Set();
    const values = variants.map((variant) => {
      const options = Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3];
      const value = String(sizeIndex >= 0 ? options[sizeIndex] || '' : variant.title || '').trim();
      return { id: variant.id, value, available: Boolean(variant.available), price: variant.price };
    }).filter((item) => {
      const key = item.value.toLowerCase().replace(/[^a-z0-9]+/g, '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => Number(b.available) - Number(a.available));
    return { sizeIndex, values };
  };
  const orjnQaCommonPrice = product => {
    const variants = product.variants || [];
    if (!variants.length || variants.some(v => v.price == null || !Number.isFinite(Number(v.price)))) return null;
    const prices = new Set(variants.map(v => Number(v.price)));
    return prices.size === 1 ? Number(variants[0].price) : null;
  };
  const orjnQaLabelHTML = (sizeIndex, price) => (sizeIndex >= 0 ? 'Select size' : 'Select option') + (price == null ? '' : ' <span class="orjn-card-qa__common-price">' + esc(orjnQaMoney(price)) + '</span>');
  const orjnQaSizesHTML = (values, showPrices = true) => values.length ? values.map((item) => {
    const priceHtml = showPrices && item.price != null ? `<span class="orjn-card-qa__size-price">${esc(orjnQaMoney(item.price))}</span>` : '';
    const money = item.price != null ? ` (${esc(orjnQaMoney(item.price))})` : '';
    return item.available
      ? `<button type="button" class="orjn-card-qa__size${showPrices ? ' orjn-card-qa__size--priced' : ''}" data-orjn-qa-size data-variant-id="${esc(item.id)}" aria-label="Add ${esc(item.value)}${money} to cart"><span class="orjn-card-qa__size-value">${esc(item.value)}</span>${priceHtml}</button>`
      : `<button type="button" class="orjn-card-qa__size${showPrices ? ' orjn-card-qa__size--priced' : ''} is-disabled" disabled aria-disabled="true"><span class="orjn-card-qa__size-value">${esc(item.value)}</span>${priceHtml}</button>`;
  }).join('') : '<span class="orjn-individual-card__qa-empty">No available options.</span>';

  const hydrateIndividualQuickAdd = async (quickAdd) => {
    if (!quickAdd || quickAdd.dataset.orjnIndividualQaState) return;
    const handle = quickAdd.dataset.productHandle;
    if (!handle) return;
    quickAdd.dataset.orjnIndividualQaState = 'loading';
    const trigger = quickAdd.querySelector('[data-orjn-individual-qa-trigger]');
    const label = quickAdd.querySelector('[data-orjn-individual-qa-label]');
    const sizes = quickAdd.querySelector('[data-orjn-individual-qa-sizes]');
    trigger?.classList.add('is-loading');
    trigger?.setAttribute('aria-busy', 'true');
    try {
      const product = await loadProductJson(handle);
      const { sizeIndex, values } = orjnQaSizeValues(product);
      const commonPrice = orjnQaCommonPrice(product);
      if (label) label.innerHTML = orjnQaLabelHTML(sizeIndex, commonPrice);
      if (sizes) sizes.innerHTML = orjnQaSizesHTML(values, commonPrice == null);
      quickAdd.dataset.orjnIndividualQaState = 'ready';
    } catch (error) {
      quickAdd.dataset.orjnIndividualQaState = 'error';
      if (label) label.textContent = 'Unable to load sizes';
      if (sizes) sizes.innerHTML = '<span class="orjn-individual-card__qa-empty">Open the product to choose a size.</span>';
      console.error('[ORJN individual quick add]', error);
    } finally {
      trigger?.classList.remove('is-loading');
      trigger?.removeAttribute('aria-busy');
    }
  };

  // Load the sizes for a group card's currently-selected colorway. The card stores the
  // active colorway handle in data-selected-handle (kept fresh by setSelectedProduct).
  const hydrateGroupQuickAdd = async (quickAdd) => {
    if (!quickAdd) return;
    const card = quickAdd.closest('.orjn-group-card');
    const handle = card && card.dataset.selectedHandle;
    if (!handle) return;
    if (quickAdd.dataset.orjnGroupQaHandle === handle && quickAdd.dataset.orjnGroupQaState === 'ready') return;
    if (quickAdd.dataset.orjnGroupQaState === 'loading' && quickAdd.dataset.orjnGroupQaHandle === handle) return;
    quickAdd.dataset.orjnGroupQaState = 'loading';
    quickAdd.dataset.orjnGroupQaHandle = handle;
    const trigger = quickAdd.querySelector('[data-orjn-group-qa-trigger]');
    const label = quickAdd.querySelector('[data-orjn-group-qa-label]');
    const sizes = quickAdd.querySelector('[data-orjn-group-qa-sizes]');
    trigger?.classList.add('is-loading');
    trigger?.setAttribute('aria-busy', 'true');
    try {
      const product = await loadProductJson(handle);
      if (quickAdd.dataset.orjnGroupQaHandle !== handle) return; // colorway changed mid-load
      const { sizeIndex, values } = orjnQaSizeValues(product);
      const commonPrice = orjnQaCommonPrice(product);
      if (label) label.innerHTML = orjnQaLabelHTML(sizeIndex, commonPrice);
      if (sizes) sizes.innerHTML = orjnQaSizesHTML(values, commonPrice == null);
      quickAdd.dataset.orjnGroupQaState = 'ready';
    } catch (error) {
      quickAdd.dataset.orjnGroupQaState = 'error';
      if (label) label.textContent = 'Unable to load sizes';
      if (sizes) sizes.innerHTML = '<span class="orjn-individual-card__qa-empty">Open the product to choose a size.</span>';
      console.error('[ORJN group quick add]', error);
    } finally {
      trigger?.classList.remove('is-loading');
      trigger?.removeAttribute('aria-busy');
    }
  };

  const setupIndividualQuickAdd = () => {
    if (document.documentElement.dataset.orjnIndividualQaBound) return;
    document.documentElement.dataset.orjnIndividualQaBound = 'true';
    document.addEventListener('click', (event) => {
      const groupTrigger = event.target.closest('[data-orjn-group-qa-trigger]');
      if (groupTrigger) { hydrateGroupQuickAdd(groupTrigger.closest('[data-orjn-group-qa]')); return; }
      const trigger = event.target.closest('[data-orjn-individual-qa-trigger]');
      if (!trigger) return;
      hydrateIndividualQuickAdd(trigger.closest('[data-orjn-individual-qa]'));
    });
  };

  const pagination = (page, pages) => {
    if (pages <= 1) return null;
    const nav = document.createElement('nav');
    nav.className = 'orjn-group-pagination';
    nav.setAttribute('aria-label', 'Grouped product pages');
    for (let index = 1; index <= pages; index += 1) {
      if (pages > 9 && Math.abs(index - page) > 2 && index !== 1 && index !== pages) {
        if (index === 2 || index === pages - 1) nav.insertAdjacentHTML('beforeend', '<span>…</span>');
        continue;
      }
      if (index === page) nav.insertAdjacentHTML('beforeend', `<span aria-current="page">${index}</span>`);
      else {
        const url = new URL(location.href);
        url.searchParams.delete('orjn_group_page');
        if (index === 1) url.searchParams.delete('page');
        else url.searchParams.set('page', String(index));
        nav.insertAdjacentHTML('beforeend', `<a href="${esc(url.pathname + url.search)}">${index}</a>`);
      }
    }
    return nav;
  };

  const renderProgressiveGrid = async (nativeGrid, type) => {
    const products = orjnFilterProducts(await loadTypeCatalog(type));
    const previewGroups = groupsForType(type, products).slice(0, 3);
    if (!previewGroups.length || !nativeGrid.isConnected || document.documentElement.classList.contains('orjn-groups-timed-out')) return;
    const grid = document.createElement('ul');
    grid.className = `${nativeGrid.className.replace('orjn-native-grid-hidden', '')} orjn-groups-grid orjn-groups-grid--progressive`;
    grid.setAttribute('role', 'list');
    grid.setAttribute('aria-label', 'Loading complete grouped catalog');
    grid.style.cssText = nativeGrid.style.cssText;
    const cards = previewGroups.map(cardMarkup);
    grid.append(...cards);
    for (let index = cards.length; index < GRID_PAGE_SIZE; index += 1) {
      const placeholder = document.createElement('li');
      placeholder.className = 'product-grid__item orjn-group-skeleton-card orjn-group-skeleton-card--reserved';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML = '<span class="orjn-group-skeleton-card__media"></span><span class="orjn-group-skeleton-card__line orjn-group-skeleton-card__line--title"></span><span class="orjn-group-skeleton-card__line orjn-group-skeleton-card__line--text"></span><span class="orjn-group-skeleton-card__line orjn-group-skeleton-card__line--controls"></span>';
      grid.appendChild(placeholder);
    }
    const criticalSkeleton = document.querySelector('.orjn-groups-skeleton');
    if (criticalSkeleton) criticalSkeleton.replaceWith(grid);
    else nativeGrid.insertAdjacentElement('afterend', grid);
    const firstReady = hydrateCard(cards[0], previewGroups[0]);
    cards.slice(1).forEach((card, index) => hydrateCard(card, previewGroups[index + 1]));
    await firstReady;
    if (cards[0].dataset.loaded === 'true') finishPending();
  };

  // ── ORJN: per-size price filtering INSIDE groups ──────────────────────────
  // When a shopper filters by a specific size (filter.p.tag) AND price
  // (filter.v.price), keep the colorway groups but include only the colorways
  // whose SELECTED size's price is in range, and show that size's price.
  // A shopper-facing size maps to a small alias group (Nike "44.5" + adidas "44 2/3"; whole "44" + "44 1/3").
  // Expanding a URL tag to its WHOLE group makes matching robust even if only one alias survives the URL
  // (Shopify can normalise/drop a duplicate `filter.p.tag`), and the whole-size "N 1/3" alias is unused in
  // practice — adidas whole sizes are just "N" — so a bare "N 1/3" must still resolve to "N".
  // Read a filter param's values, splitting on comma. Our in-page filters write ONE comma-joined param
  // per facet (Shopify keeps single params on reload but drops duplicate params, keeping only the last).
  const ORJN_LITERAL_COMMA_PARAMS = new Set([
    'filter.p.m.custom.gender',
    'filter.p.m.custom.football_cleats',
  ]);
  const orjnUrlList = (param) => new URLSearchParams(location.search).getAll(param)
    .flatMap((v) => ORJN_LITERAL_COMMA_PARAMS.has(param) ? [String(v)] : String(v).split(','))
    .map((s) => s.trim()).filter(Boolean);
  const orjnSizeGroup = (tag) => {
    const m = String(tag || '').trim().match(/^(\d{1,2})(\.5)?( ?([12])\/3)?$/);
    if (!m) return [String(tag || '').trim()];
    const w = parseInt(m[1], 10);
    return (m[2] === '.5' || m[4] === '2') ? [w + '.5', w + ' 2/3'] : ['' + w, w + ' 1/3'];
  };
  // Memoized by the URL query string: this is a pure function of the URL, but it was being called per
  // product (orjnSortPrice comparator) and per card (orjnPriceHTML) — thousands of times per render, each
  // re-parsing the same URL (a big share of client-side load time in profiling). The URL is constant during
  // a render; recompute only when it actually changes (a filter navigation updates location.search).
  let orjnSizeStateKey = null, orjnSizeStateVal = null;
  const orjnSizeState = () => {
    const search = location.search;
    if (orjnSizeStateKey === search) return orjnSizeStateVal;
    const params = new URLSearchParams(search);
    const raw = orjnUrlList('filter.p.tag').filter((t) => ORJN_SIZE_LIKE.test(t));
    let result;
    if (!raw.length) {
      result = null; // a selected size alone drives the per-size price shown; a price range additionally filters by it
    } else {
      const sizes = [...new Set(raw.flatMap(orjnSizeGroup))];
      const toC = (v) => { if (v == null || v === '') return null; const n = parseFloat(String(v).replace(/[^0-9.]/g, '')); return isNaN(n) ? null : Math.round(n * 100); };
      const gte = toC(params.get('filter.v.price.gte'));
      const lte = toC(params.get('filter.v.price.lte'));
      result = { sizes, min: gte == null ? -Infinity : gte, max: lte == null ? Infinity : lte, hasRange: gte != null || lte != null };
    }
    orjnSizeStateKey = search;
    orjnSizeStateVal = result;
    return result;
  };
  const orjnParseSizePrices = (str) => { const m = {}; (str || '').split(';').forEach((pair) => { const i = pair.lastIndexOf(':'); if (i < 0) return; const s = pair.slice(0, i).trim(); const v = parseInt(pair.slice(i + 1), 10); if (s && !isNaN(v) && (m[s] == null || v < m[s])) m[s] = v; }); return m; };
  const orjnDistinctSizePrices = (product) => { const m = orjnParseSizePrices(product && product.sizePrices); const u = new Set(); for (const k in m) u.add(m[k]); return [...u]; }; // distinct per-size prices (cents) → "From" when >1
  const orjnSizePriceInfo = (product, state) => {
    const priceMap = orjnParseSizePrices(product && product.sizePrices);
    const compareMap = orjnParseSizePrices(product && product.sizeCompareAt);
    let best = null, bestSize = null; const inRange = new Set();
    for (const s of state.sizes) { const pr = priceMap[s]; if (pr != null && pr >= state.min && pr <= state.max) { inRange.add(pr); if (best == null || pr < best) { best = pr; bestSize = s; } } }
    if (best == null) return null;
    const cmp = bestSize != null ? compareMap[bestSize] : null;
    return { price: best, compareAt: (cmp != null && cmp > best) ? cmp : null, varies: inRange.size > 1 }; // selected size's in-range price + its own compare-at; varies = more than one in-range selected size
  };
  const orjnSizePriceFor = (product, state) => { const info = orjnSizePriceInfo(product, state); return info ? info.price : null; }; // lowest in-range price (cents) for the selected size(s), or null
  const orjnFilterProducts = (products) => {
    const state = orjnSizeState();
    if (!state || !state.hasRange) return products; // size alone only changes the price shown; a price range removes out-of-range colorways
    return (products || []).filter((p) => orjnSizePriceFor(p, state) != null);
  };
  // Sort key for ordering colorways within a group cheapest-first: the SELECTED size's price when a
  // size is chosen, otherwise the colorway's lowest price (product.price = min variant price).
  const orjnSortPrice = (product) => {
    const state = orjnSizeState();
    if (state) { const info = orjnSizePriceInfo(product, state); if (info && info.price != null) return info.price; }
    return Number((product && product.price) || 0);
  };
  const orjnByPriceAsc = (a, b) => (orjnSortPrice(a) - orjnSortPrice(b)) || (Number(b.createdAt || 0) - Number(a.createdAt || 0)); // cheapest first; ties → newest
  const orjnMoney = (template, cents) => {
    const num = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return ((template || '').replace(/[\d.,]+/, num)) || ('$' + num);
  };
  // One price renderer for both card types. When a size is selected it shows THAT size's price
  // (exact, no "From"); "From" appears only when the shown price actually spans more than one
  // value — multiple selected sizes, or (no size selected) a colorway whose sizes vary.
  const orjnPriceHTML = (product, groupStyle) => {
    const fromTag = '<span class="orjn-price-from">From </span>';
    const wrapCur = (s) => groupStyle ? `<span class="orjn-group-card__sale-price">${s}</span>` : s;
    const wrapOld = (s) => groupStyle ? `<s class="orjn-group-card__compare-price">${s}</s>` : `<s>${s}</s>`;
    const state = orjnSizeState();
    const info = state ? orjnSizePriceInfo(product, state) : null;
    if (info) {
      const cur = esc(orjnMoney(product.priceMoney, info.price));
      const pre = info.varies ? fromTag : '';
      return info.compareAt != null
        ? `${pre}${wrapCur(cur)} ${wrapOld(esc(orjnMoney(product.compareAtPriceMoney || product.priceMoney, info.compareAt)))}`
        : `${pre}${cur}`;
    }
    const pre = orjnDistinctSizePrices(product).length > 1 ? fromTag : '';
    const prices = orjnParseSizePrices(product.sizePrices);
    const compares = orjnParseSizePrices(product.sizeCompareAt);
    const ownCompare = Object.keys(prices)
      .filter((size) => prices[size] === Number(product.price) && compares[size] > prices[size])
      .reduce((best, size) => Math.max(best, compares[size]), 0) || null;
    if (ownCompare != null) return `${pre}${wrapCur(esc(product.priceMoney || ''))} ${wrapOld(esc(orjnMoney(product.compareAtPriceMoney || product.priceMoney, ownCompare)))}`;
    if (!Object.keys(prices).length && isSaleProduct(product) && product.compareAtPriceMoney)
      return `${pre}${wrapCur(esc(product.priceMoney || ''))} ${wrapOld(esc(product.compareAtPriceMoney))}`;
    return `${pre}${esc(product.priceMoney || '')}`;
  };

  // ===================================================================================================
  // Client-side collection filters. Every collection/search grid that the ORJN grouping engine owns uses
  // one full, cached catalog and filters it in the browser. This is the fast path Lifestyle already used;
  // applying it consistently avoids a full Shopify document + feed reload on Basketball/Running filters.
  // Non-groupable collections (notably Football) bail before this block and retain their native filters.
  // ===================================================================================================
  const orjnClientFilterMode = () =>
    enabled() && !location.pathname.startsWith('/products/')
    && !!document.querySelector('.main-collection-grid .product-grid, results-list .product-grid');

  // Model facet param for THIS collection (whichever type its products carry).
  const orjnModelParam = (catalog) => {
    for (const p of (catalog || [])) if (TYPE_PARAMS.includes(p.groupParam) && p.groupValue && !p.ageRange) return p.groupParam;
    return null;
  };
  const orjnModelLabel = (param) => ({
    'filter.p.m.custom.lifestyle_type': 'Lifestyle Type',
    'filter.p.m.custom.basketball_type': 'Basketball Type',
    'filter.p.m.custom.running_type': 'Running Type',
    'filter.p.m.custom.training_type': 'Training Type',
    'filter.p.m.custom.tennis_type': 'Tennis Type',
  }[param] || 'Model');
  // Customer-facing model-facet threshold (mirrors snippets/list-filter.liquid): show a model option only
  // once enough products carry it, so the filter isn't cluttered with tiny options. DISPLAY ONLY — grouping
  // reads the metafield/feed independently, so every model value still groups regardless of this.
  const orjnModelThreshold = (param) => ({
    'filter.p.m.custom.lifestyle_type': 30,
    'filter.p.m.custom.running_type': 20,
    'filter.p.m.custom.training_type': 5,
    'filter.p.m.custom.tennis_type': 15,
  }[param] || 15);
  // Model FACET only (not grouping): basketball_type is never offered as a filter option — basketball is
  // filtered by Player natively, and its type facet is hidden. Grouping still uses the full TYPE_PARAMS.
  const MODEL_FACET_PARAMS = TYPE_PARAMS.filter((p) => !/basketball_type/.test(p));
  const ORJN_SIMPLE_FACET_PARAMS = {
    brand: 'filter.p.vendor',
    productType: 'filter.p.product_type',
    sport: 'filter.p.m.custom.clothing_sport',
    color: 'filter.p.m.custom.color',
    age: 'filter.p.m.custom.age_range',
    player: 'filter.p.m.custom.basketball_player',
    football: 'filter.p.m.custom.football_cleats',
  };
  const ORJN_USER_FACETS = ['sizeprice', 'brand', 'productType', 'color', 'sport', 'model', 'age', 'player', 'football'];

  const orjnActiveFilters = () => {
    // Legacy simple facets may be comma-joined. orjnUrlList deliberately preserves the real commas in
    // gender and football-cleat values, so one metafield value cannot become two fake filters.
    const vals = (name) => orjnUrlList(name);
    const models = [];
    TYPE_PARAMS.forEach((tp) => vals(tp).forEach((v) => models.push(v)));
    return {
      brand: vals('filter.p.vendor'),
      productType: vals('filter.p.product_type'),
      sport: new URLSearchParams(location.search).getAll('filter.p.m.custom.clothing_sport').map(v => v.trim()).filter(Boolean),
      color: vals('filter.p.m.custom.color'),
      gender: vals('filter.p.m.custom.gender'),
      // Shared by Clothing and Accessories; preserve exact category values (including commas).
      clothingType: new URLSearchParams(location.search).getAll('filter.p.m.custom.clothing_type').map(v => v.trim()).filter(Boolean),
      model: models,
      age: vals('filter.p.m.custom.age_range'),
      player: vals('filter.p.m.custom.basketball_player'),
      football: vals('filter.p.m.custom.football_cleats'),
    };
  };
  // Narrow by brand/color/gender/model + size existence (client-side); pass `skip` to ignore one facet
  // (for faceted counts). Size narrowing keeps products that COME IN a selected size (any alias) — the
  // "hard filter to your size" behaviour that Shopify's tag filter gives small collections server-side.
  const narrowByFilters = (products, skip) => {
    const f = orjnActiveFilters();
    // Gender: the MEN/WOMEN nav sends a specific gender + the unisex "Men, Women"; on big collections
    // Shopify collapses these (usually to a lone "Men, Women"), so we can't always recover the specific
    // gender. But a gendered browse is ALWAYS an ADULT browse — so whenever any gender filter is present we
    // drop kids (ageRange populated), and, when a specific gender survived, also narrow to it (+ unisex).
    // This keeps kids products AND kids-only sizes out of MEN/WOMEN views without ever emptying the page
    // (adults always exist); ungendered browsing (direct /collections/lifestyle) still shows everyone.
    const genderActive = f.gender.length > 0;
    const genders = f.gender.filter((g) => g === 'Men' || g === 'Women'); // specific values only (lone unisex → adults, no specific narrowing)
    const params = new URLSearchParams(location.search);
    const sizes = skip === 'sizeprice' ? [] : [...new Set(orjnUrlList('filter.p.tag').filter((t) => ORJN_SIZE_LIKE.test(t)).flatMap(orjnSizeGroup))];
    // Price WITHOUT a selected size: keep products with any size price in range (matches Shopify's native
    // filter.v.price). WITH a size, orjnFilterProducts applies the price to that size instead.
    let pmin = -Infinity, pmax = Infinity, hasPrice = false;
    if (skip !== 'sizeprice' && !sizes.length) {
      const g = parseFloat(params.get('filter.v.price.gte')); const l = parseFloat(params.get('filter.v.price.lte'));
      if (!isNaN(g)) { pmin = g * 100; hasPrice = true; } if (!isNaN(l)) { pmax = l * 100; hasPrice = true; }
    }
    return (products || []).filter((p) => {
      // Category is a browse constraint, not a shoe-model group or a clearable shopping facet.
      if (f.clothingType.length && !f.clothingType.includes(p.clothingType)) return false;
      if (skip !== 'sport' && f.sport.length && !f.sport.some(v => (Array.isArray(p.clothingSport) ? p.clothingSport : [p.clothingSport]).includes(v))) return false;
      if (skip !== 'brand' && f.brand.length && !f.brand.includes(p.brand)) return false;
      if (skip !== 'productType' && f.productType.length && !f.productType.includes(p.productType)) return false;
      if (skip !== 'color' && f.color.length && !f.color.includes(p.color)) return false;
      if (skip !== 'gender' && genderActive) {
        if (p.ageRange) return false; // gendered browse = adults only (kids have their own CHILDREN nav)
        if (genders.length && !genders.some(g => window.ORJNDepartmentGenderMatches(p, g))) return false; // shared gender policy, including neutral accessories
      }
      if (skip !== 'model' && f.model.length && !f.model.includes(p.groupValue)) return false;
      if (skip !== 'age' && f.age.length && !f.age.includes(p.ageRange)) return false;
      if (skip !== 'player' && f.player.length && !f.player.includes(p.basketballPlayer)) return false;
      if (skip !== 'football' && f.football.length && !f.football.includes(p.footballCleats)) return false;
      if (sizes.length) { const m = orjnParseSizePrices(p.sizePrices); if (!sizes.some((s) => m[s] != null)) return false; }
      if (hasPrice) { const m = orjnParseSizePrices(p.sizePrices); const vals = Object.keys(m).map((k) => m[k]); const base = vals.length ? vals : [Number(p.price) || 0]; if (!base.some((v) => v >= pmin && v <= pmax)) return false; }
      return true;
    });
  };
  // Full client-side narrowing = brand/color/gender/model + size/price (orjnFilterProducts reads the URL).
  const orjnApplyFilters = (products) => orjnFilterProducts(narrowByFilters(products));

  // Per-option counts for one facet, respecting every OTHER active facet + size/price (like native facets).
  const orjnFacetCounts = (catalog, facetKey, field) => {
    const base = orjnFilterProducts(narrowByFilters(catalog, facetKey));
    const counts = new Map();
    base.forEach((p) => {
      let v;
      if (facetKey === 'model') { if (!MODEL_FACET_PARAMS.includes(p.groupParam) || p.ageRange) return; v = p.groupValue; } // only groupable models offered as a facet (excl. football, kids, basketball_type)
      else v = p[field];
      for (const value of new Set(Array.isArray(v) ? v : [v])) if (value != null && value !== '') counts.set(value, (counts.get(value) || 0) + 1);
    });
    return counts;
  };
  // model value -> its type param (for mixed brand collections that span lifestyle/basketball/running/…)
  const orjnModelParamMap = (catalog) => {
    const m = new Map();
    (catalog || []).forEach((p) => { if (MODEL_FACET_PARAMS.includes(p.groupParam) && p.groupValue && !p.ageRange && !m.has(p.groupValue)) m.set(p.groupValue, p.groupParam); });
    return m;
  };

  // -- URL helpers: our filter UI writes standard filter params, then re-renders in place (no reload). --
  const orjnSetParam = (param, values) => {
    const url = new URL(location.href);
    url.searchParams.delete(param);
    const list = (values || []).filter(Boolean);
    if (ORJN_LITERAL_COMMA_PARAMS.has(param)) list.forEach((value) => url.searchParams.append(param, value));
    else if (list.length) url.searchParams.set(param, list.join(',')); // legacy simple facets use one stable comma-joined param
    url.searchParams.delete('page'); url.searchParams.delete('orjn_group_page'); // any filter change → page 1
    history.replaceState({}, '', url);
  };
  const orjnToggleValue = (param, value) => {
    // Read every repeated parameter so changing one CHILDREN age never discards the other age locks.
    const cur = orjnUrlList(param);
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : cur.concat(value);
    orjnSetParam(param, next);
  };

  let orjnGridCatalogCache = null; // parsed feed kept in memory so re-filtering is instant (no re-fetch)
  let orjnGridCatalogSortKey = null; // best-selling needs Shopify's source rank; every other sort uses local fields
  const orjnReRenderGrid = async () => {
    const nativeGrid = document.querySelector('.main-collection-grid .product-grid:not(.orjn-groups-grid), results-list .product-grid:not(.orjn-groups-grid)');
    if (nativeGrid) nativeGrid.dataset.orjnGroupsSource = ''; // allow mountGrid to run again
    await mountGrid();
  };
  window.ORJNApplySortInPlace = async (value) => {
    const allowed = new Set(['best-selling', 'price-ascending', 'price-descending', 'created-ascending', 'created-descending']);
    if (!allowed.has(value) || !document.documentElement.classList.contains('orjn-client-filters')) return false;
    const url = new URL(location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('_orjn_size_page');
    url.searchParams.delete('orjn_group_page');
    history.replaceState({}, '', url);
    if (value === 'best-selling' && orjnGridCatalogSortKey !== 'best-selling') {
      // Best-selling is the only order we cannot derive from price/date fields. Load its canonical ranked
      // feed once (without a document reload); the previous grid remains visible until the swap is ready.
      orjnGridCatalogCache = null;
      orjnGridCatalogSortKey = null;
    }
    document.querySelectorAll('sorting-filter-component').forEach((component) => {
      let selectedName = '';
      component.querySelectorAll('input[name="sort_by"]').forEach((input) => {
        const selected = input.value === value;
        input.checked = selected;
        const option = input.closest('[role="option"]');
        if (option) { option.setAttribute('aria-selected', String(selected)); option.tabIndex = selected ? 0 : -1; }
        if (selected) selectedName = input.dataset.optionName || input.value;
      });
      component.querySelectorAll('select[name="sort_by"]').forEach((select) => { select.value = value; });
      component.querySelectorAll('.sorting-filter__status').forEach((status) => { status.textContent = selectedName; });
      component.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
      component.querySelectorAll('[aria-expanded="true"]').forEach((summary) => summary.setAttribute('aria-expanded', 'false'));
    });
    await orjnReRenderGrid();
    return true;
  };

  // ---- Build + inject the filter bar on every ORJN-managed grid ----
  const orjnEsc = (s) => esc(String(s == null ? '' : s));
  const orjnCanonicalSizeLabel = (tag) => {
    const m = String(tag || '').trim().match(/^(\d{1,2})(\.5)?( ?([12])\/3)?$/);
    if (!m) return String(tag || '').trim();
    const whole = parseInt(m[1], 10);
    return (m[2] === '.5' || m[4] === '2') ? (whole + '.5') : String(whole);
  };
  const orjnSelectedSizeLabels = () => [...new Set(orjnUrlList('filter.p.tag')
    .filter((tag) => ORJN_SIZE_LIKE.test(tag)).map(orjnCanonicalSizeLabel))]
    .sort(orjnSizeCmp);
  let orjnFilterBarBuilt = false;

  const orjnAllSizes = (catalog) => {
    const set = new Set();
    (catalog || []).forEach((p) => { const m = orjnParseSizePrices(p.sizePrices); for (const s in m) if (ORJN_SIZE_LIKE.test(s)) set.add(s); });
    // one shopper-facing size per alias group: prefer the "N.5"/"N" form, drop the "N 2/3"/"N 1/3" twin
    const canonical = [...set].filter((s) => !/^\d{1,2} ?[12]\/3$/.test(s));
    return canonical.sort(orjnSizeCmp);
  };
  // Nike "44.5" also matches adidas "44 2/3"; whole "44" also matches "44 1/3" (see docs 04 alias map).
  const orjnSizeAliases = (size) => {
    const n = parseFloat(size);
    if (/\.5$/.test(size)) return [size, Math.floor(n) + ' 2/3'];
    if (/^\d+$/.test(size)) return [size, size + ' 1/3'];
    return [size];
  };
  // Raw size tags that actually EXIST among the products matching every active filter EXCEPT size/price
  // (like native's dynamic size-availability, but computed in-browser so it also works on big collections
  // where Shopify can't filter). The size picker shows only these — no 0-result sizes.
  const orjnAvailableSizes = (catalog) => {
    const set = new Set();
    narrowByFilters(catalog || [], 'sizeprice').forEach((p) => { const m = orjnParseSizePrices(p.sizePrices); for (const s in m) if (ORJN_SIZE_LIKE.test(s)) set.add(s); });
    return set;
  };

  // A size filter is useful only when the current category/facets offer a choice.
  // Keep an active size visible so an old/deep-linked selection remains removable.
  const orjnHasSizeChoice = (catalog) => {
    if (orjnSelectedSizeLabels().length) return true;
    return new Set([...orjnAvailableSizes(catalog)].map(s => orjnCanonicalSizeLabel(s).toUpperCase())).size > 1;
  };
  const orjnSyncSizeChoice = (cf, catalog) => {
    const hasChoice = orjnHasSizeChoice(catalog);
    cf.classList.toggle('orjn-cf--price-only', !hasChoice);
    const summary = cf.querySelector('.orjn-cf__summary');
    const label = summary && [...summary.childNodes].find(n => n.nodeType === 3);
    if (label) label.textContent = hasChoice ? 'Size & Price' : 'Price';
    const params = new URLSearchParams(location.search);
    cf.querySelectorAll('.orjn-cf__price-in').forEach(input => {
      input.value = params.get('filter.v.price.' + input.dataset.price) || '';
    });
  };

  const orjnFacetPanel = (facetKey, field, counts, activeVals, paramMap) => {
    activeVals.forEach((value) => { if (!counts.has(value)) counts.set(value, 0); });
    let rows = [...counts.entries()].sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0])); // busiest-first, like native model/player facets
    if (facetKey === 'model') {
      // Show a model option only when enough products carry it (per-type threshold, mirrors list-filter.liquid);
      // always keep an active/selected value so a deep-linked filter stays removable. DISPLAY ONLY — grouping is unaffected.
      rows = rows.filter(([val, count]) => activeVals.includes(val) || count >= orjnModelThreshold(paramMap && paramMap.get(val)));
    }
    if (!rows.length) return '<div class="orjn-cf__empty">No options</div>';
    return '<div class="orjn-cf__list">' + rows.map(([val, count]) =>
      '<label class="orjn-cf__opt' + (activeVals.includes(val) ? ' is-checked' : '') + '">' +
      '<input type="checkbox" ' + (activeVals.includes(val) ? 'checked' : '') + ' data-facet="' + facetKey + '"' + (paramMap && paramMap.get(val) ? ' data-param="' + orjnEsc(paramMap.get(val)) + '"' : '') + ' value="' + orjnEsc(val) + '">' +
      '<span class="orjn-cf__box"></span><span class="orjn-cf__name">' + orjnEsc(val) + '</span>' +
      '<span class="orjn-cf__count">' + count + '</span></label>').join('') + '</div>';
  };

  const orjnSizePricePanel = (catalog) => {
    const params = new URLSearchParams(location.search);
    const activeSizes = orjnUrlList('filter.p.tag').filter((t) => ORJN_SIZE_LIKE.test(t));
    const avail = orjnAvailableSizes(catalog);
    // Only sizes present in the current view (or already selected, so a deep link stays removable).
    const sizes = orjnAllSizes(catalog); // Keep hidden alternatives so clearing another facet can restore them.
    const chips = sizes.map((s) =>
      '<button type="button"' + (orjnSizeAliases(s).some(a => activeSizes.includes(a) || avail.has(a)) ? '' : ' hidden') + ' class="orjn-cf__size' + (orjnSizeAliases(s).some((a) => activeSizes.includes(a)) ? ' is-on' : '') + '" data-size="' + orjnEsc(s) + '">' + orjnEsc(s) + '</button>').join('');
    const gte = params.get('filter.v.price.gte') || '';
    const lte = params.get('filter.v.price.lte') || '';
    return '<div class="orjn-cf__sp">' +
      '<div class="orjn-cf__sp-head">Choose your size <span class="orjn-cf__sp-hint">select 1 or more</span></div>' +
      '<div class="orjn-cf__sizes">' + (chips || '<span class="orjn-cf__empty">No sizes</span>') + '</div>' +
      '<div class="orjn-cf__sp-head orjn-cf__sp-head--price">Price</div>' +
      '<div class="orjn-cf__price"><input type="number" inputmode="numeric" class="orjn-cf__price-in" data-price="gte" placeholder="Min" value="' + orjnEsc(gte) + '"><span>–</span>' +
      '<input type="number" inputmode="numeric" class="orjn-cf__price-in" data-price="lte" placeholder="Max" value="' + orjnEsc(lte) + '"></div>' +
      '</div>';
  };

  const orjnActiveCount = (facetKey) => {
    const f = orjnActiveFilters();
    if (Array.isArray(f[facetKey])) return f[facetKey].length;
    if (facetKey === 'sizeprice') { const params = new URLSearchParams(location.search); const sz = orjnSelectedSizeLabels().length; return sz + ((params.get('filter.v.price.gte') || params.get('filter.v.price.lte')) ? 1 : 0); }
    return 0;
  };

  // One definition builder feeds desktop and mobile, keeping every customer-facing native facet that is
  // meaningful on ORJN-managed grids. A one-choice facet stays hidden unless active, matching native UI.
  const orjnFilterSections = (catalog) => {
    const active = orjnActiveFilters();
    const sections = [{ key: 'sizeprice', label: orjnHasSizeChoice(catalog) ? 'Size & Price' : 'Price', panel: orjnSizePricePanel(catalog) }];
    const addSimple = (key, label, field) => {
      const counts = orjnFacetCounts(catalog, key, field);
      const activeVals = active[key] || [];
      if (counts.size <= 1 && !activeVals.length) return;
      sections.push({ key, label, panel: orjnFacetPanel(key, field, counts, activeVals) });
    };
    addSimple('brand', 'Brand', 'brand');
    addSimple('productType', 'Product Type', 'productType');
    addSimple('color', 'Color', 'color');
    addSimple('sport', 'Sport', 'clothingSport');

    const modelMap = orjnModelParamMap(catalog);
    const modelParams = [...new Set(modelMap.values())];
    const modelParam = modelParams[0] || '';
    const modelLabel = modelParams.length > 1 ? 'Model' : (modelParams.length ? orjnModelLabel(modelParam) : '');
    const modelCounts = orjnFacetCounts(catalog, 'model', 'groupValue');
    const modelVisible = [...modelCounts].some(([value, count]) =>
      active.model.includes(value) || count >= orjnModelThreshold(modelMap.get(value)));
    if (modelParams.length && (modelVisible || active.model.length)) {
      sections.push({
        key: 'model', label: modelLabel, modelParam,
        panel: orjnFacetPanel('model', 'groupValue', modelCounts, active.model, modelMap),
      });
    }
    addSimple('age', 'Age Range', 'ageRange');
    addSimple('player', 'Basketball Player', 'basketballPlayer');
    addSimple('football', 'Football Cleats', 'footballCleats');
    return sections;
  };

  const orjnRenderFilterBar = (catalog) => {
    const host = document.querySelector('.facets .facets__form-wrapper, .facets facets-form-component, .facets');
    if (!host) return;
    // Build once; afterwards only refresh counts/state in place so open panels aren't torn down.
    if (document.querySelector('[data-orjn-cfilters]:not(.orjn-cdrawer)')) return;
    const facets = orjnFilterSections(catalog);
    const html = '<div class="orjn-cfilters" data-orjn-cfilters>' + facets.map((f) => {
      const n = orjnActiveCount(f.key);
      return '<div class="orjn-cf" data-facet="' + f.key + '">' +
        '<button type="button" class="orjn-cf__summary" aria-expanded="false">' + orjnEsc(f.label) +
        '<span class="orjn-cf__badge"' + (n ? '' : ' hidden') + '>' + n + '</span>' +
        '<svg class="orjn-cf__caret" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<div class="orjn-cf__panel" data-model-param="' + orjnEsc(f.modelParam || '') + '">' + f.panel +
        '<div class="orjn-cf__foot"><button type="button" class="orjn-cf__clear"' + (n ? '' : ' hidden') + '>Clear</button><button type="button" class="orjn-cf__done">Done</button></div>' +
        '</div></div>';
    }).join('') + '<button type="button" class="orjn-cf-clearall" data-orjn-cf-clearall hidden>Clear all</button></div>';
    let bar = host.querySelector('[data-orjn-cfilters]');
    if (bar) bar.outerHTML = html; else host.insertAdjacentHTML('afterbegin', html);
    orjnWireFilterBar(catalog);
    orjnFilterBarBuilt = true;
  };

  // The theme prints its own "X items" count server-side = the WHOLE collection (Shopify can't apply our
  // filters/gender on a big collection). If a filter IS active, that number is wrong and would visibly flash
  // (e.g. 6,949 → 2,520) once our engine finishes and corrects it. So during the feed load, when a filter is
  // active, replace the count with a "…" placeholder; orjnSyncFilterBar then fills the real number. With NO
  // filter the theme count is already correct, so we leave it untouched (no flash to fix).
  const orjnCountSpans = () => {
    // Tag the count span the first time so later calls still find it even after we've replaced its text with
    // "…" (a text-based search for "items" would then miss it — the bug this avoids).
    const tagged = [...document.querySelectorAll('[data-orjn-count]')];
    if (tagged.length) return tagged;
    const out = [];
    document.querySelectorAll('.products-count-wrapper, [class*="products-count"], [class*="product-count"]').forEach((wrap) => {
      const span = [wrap].concat([...wrap.querySelectorAll('*')]).find((e) => e.children.length === 0 && /\bitems?\b/i.test(e.textContent || ''));
      if (span) { span.setAttribute('data-orjn-count', ''); out.push(span); }
    });
    return out;
  };
  const orjnAnyFilterActive = () => orjnActiveFilters().clothingType.length > 0 || orjnActiveFilters().gender.length > 0
    || ORJN_USER_FACETS.reduce((s, k) => s + orjnActiveCount(k), 0) > 0;
  const orjnMarkCountLoading = () => {
    if (!orjnAnyFilterActive()) return;
    orjnCountSpans().forEach((span) => {
      if (span.dataset.orjnOrig == null) span.dataset.orjnOrig = span.textContent; // keep the real theme count for the "cleared" restore path
      span.textContent = '…';
    });
  };

  // Re-sync counts/active state across ALL bars (desktop + mobile drawer) in place; open panels survive.
  const orjnSyncFilterBar = () => {
    if (!document.querySelector('[data-orjn-cfilters]')) return;
    const catalog = orjnGridCatalogCache || [];
    const active = orjnActiveFilters();
    const params = new URLSearchParams(location.search);
    const countsBy = {
      brand: orjnFacetCounts(catalog, 'brand', 'brand'),
      productType: orjnFacetCounts(catalog, 'productType', 'productType'),
      color: orjnFacetCounts(catalog, 'color', 'color'),
      sport: orjnFacetCounts(catalog, 'sport', 'clothingSport'),
      model: orjnFacetCounts(catalog, 'model', 'groupValue'),
      age: orjnFacetCounts(catalog, 'age', 'ageRange'),
      player: orjnFacetCounts(catalog, 'player', 'basketballPlayer'),
      football: orjnFacetCounts(catalog, 'football', 'footballCleats'),
    };
    const activeSizes = orjnUrlList('filter.p.tag').filter((t) => ORJN_SIZE_LIKE.test(t));
    const availSizes = orjnAvailableSizes(catalog); // sizes present in the current view — hide the rest live
    document.querySelectorAll('[data-orjn-cfilters] .orjn-cf').forEach((cf) => {
      const key = cf.dataset.facet;
      const badge = cf.querySelector('.orjn-cf__badge'); const n = orjnActiveCount(key);
      if (badge) { badge.textContent = n; badge.hidden = !n; }
      const clr = cf.querySelector('.orjn-cf__clear'); if (clr) clr.hidden = !n; // native shows Clear only when the facet is active
      if (countsBy[key]) {
        const counts = countsBy[key]; const activeVals = active[key] || [];
        cf.querySelectorAll('.orjn-cf__opt').forEach((opt) => {
          const cb = opt.querySelector('input'); const val = cb.value; const c = counts.get(val) || 0;
          const cEl = opt.querySelector('.orjn-cf__count'); if (cEl) cEl.textContent = c;
          const checked = activeVals.includes(val);
          cb.checked = checked; opt.classList.toggle('is-checked', checked);
          opt.classList.toggle('orjn-cf__opt--zero', c === 0 && !checked);
          if (key === 'model') opt.hidden = !checked && c < orjnModelThreshold(cb.dataset.param); // re-apply the per-type threshold as filters narrow
        });
      } else if (key === 'sizeprice') {
        orjnSyncSizeChoice(cf, catalog);
        cf.querySelectorAll('.orjn-cf__size').forEach((chip) => {
          const aliases = orjnSizeAliases(chip.dataset.size);
          const sel = aliases.some((a) => activeSizes.includes(a));
          chip.classList.toggle('is-on', sel);
          chip.hidden = !sel && !aliases.some((a) => availSizes.has(a)); // hide 0-result sizes (keep selected)
        });
      }
    });
    // Reflect the filtered product total in the theme's "X items" label — but ONLY when a filter is
    // active, so an unfiltered view keeps the theme's own count (some collections' feeds are broader than
    // the storefront collection count, e.g. new-in).
    const facetActive = ORJN_USER_FACETS.reduce((s, k) => s + orjnActiveCount(k), 0);
    const anyFilter = facetActive > 0 || active.gender.length > 0 || active.clothingType.length > 0; // gender/kids narrowing counts too, so the label matches the grid
    const filtered = anyFilter ? orjnApplyFilters(catalog).length : 0;
    orjnCountSpans().forEach((span) => {
      if (span.dataset.orjnOrig == null) span.dataset.orjnOrig = span.textContent; // capture the theme's original count once
      span.textContent = anyFilter ? (filtered + (filtered === 1 ? ' item' : ' items')) : span.dataset.orjnOrig; // restore original when cleared
    });
    document.documentElement.classList.remove('orjn-count-pending'); // the real count is set → reveal it (drops the loader's "…" placeholder)
    document.querySelectorAll('.orjn-cf-mobtrigger__n').forEach((n) => { n.textContent = facetActive; n.hidden = !facetActive; }); // badge = user-picked facets only
    document.querySelectorAll('[data-orjn-cf-clearall]').forEach((b) => { b.hidden = !facetActive; }); // "Clear all" shows only when a filter is applied (like basketball)
    orjnSyncPriceNote(); // keep the "prices vary / size applied" banner in sync with the size selection
  };

  let orjnBarWired = false;
  const orjnWireFilterBar = (catalog, root, isDrawer) => {
    const bar = root || document.querySelector('[data-orjn-cfilters]');
    if (!bar) return;
    const closeAll = (except) => bar.querySelectorAll('.orjn-cf').forEach((cf) => { if (cf !== except) { cf.classList.remove('is-open'); const s = cf.querySelector('.orjn-cf__summary'); if (s) s.setAttribute('aria-expanded', 'false'); } });
    // Desktop dropdowns and mobile drawer sections both keep one panel open at a time.
    // The drawer starts fully collapsed so opening Filters shows a compact category list.
    bar.querySelectorAll('.orjn-cf__summary').forEach((btn) => btn.addEventListener('click', (e) => {
      e.preventDefault(); const cf = btn.closest('.orjn-cf'); const open = cf.classList.contains('is-open');
      closeAll(cf); cf.classList.toggle('is-open', !open); btn.setAttribute('aria-expanded', String(!open));
    }));
    // outside click closes desktop dropdowns (once, globally)
    if (!isDrawer && !orjnBarWired) { document.addEventListener('click', (e) => { if (!e.target.closest('[data-orjn-cfilters]')) document.querySelectorAll('[data-orjn-cfilters]:not(.orjn-cdrawer) .orjn-cf.is-open').forEach((cf) => { cf.classList.remove('is-open'); }); }); orjnBarWired = true; }
    // checkbox facets (brand/product type/color/model/age/player/football)
    bar.querySelectorAll('input[type="checkbox"][data-facet]').forEach((cb) => cb.addEventListener('change', () => {
      const key = cb.dataset.facet; const val = cb.value;
      const param = ORJN_SIMPLE_FACET_PARAMS[key]
        || cb.dataset.param || cb.closest('.orjn-cf__panel').dataset.modelParam || orjnModelParam(catalog);
      orjnToggleValue(param, val);
      orjnReRenderGrid();
    }));
    // size chips
    bar.querySelectorAll('.orjn-cf__size').forEach((chip) => chip.addEventListener('click', () => {
      const size = chip.dataset.size;
      let tags = orjnUrlList('filter.p.tag');
      const aliases = orjnSizeAliases(size); const on = aliases.some((a) => tags.includes(a));
      tags = tags.filter((t) => !aliases.includes(t)); if (!on) tags = tags.concat(aliases);
      orjnSetParam('filter.p.tag', tags); chip.classList.toggle('is-on', !on); orjnReRenderGrid();
    }));
    // price inputs (apply on change/enter)
    const applyPrice = () => {
      const gte = bar.querySelector('.orjn-cf__price-in[data-price="gte"]'); const lte = bar.querySelector('.orjn-cf__price-in[data-price="lte"]');
      const url = new URL(location.href);
      const setP = (k, el) => { const v = el && el.value.trim(); if (v) url.searchParams.set(k, v); else url.searchParams.delete(k); };
      setP('filter.v.price.gte', gte); setP('filter.v.price.lte', lte);
      url.searchParams.delete('page'); history.replaceState({}, '', url); orjnReRenderGrid();
    };
    bar.querySelectorAll('.orjn-cf__price-in').forEach((el) => { el.addEventListener('change', applyPrice); el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); applyPrice(); } }); });
    // per-facet clear + done
    bar.querySelectorAll('.orjn-cf__clear').forEach((btn) => btn.addEventListener('click', () => {
      const cf = btn.closest('.orjn-cf'); if (!cf) return; const key = cf.dataset.facet; const url = new URL(location.href);
      if (ORJN_SIMPLE_FACET_PARAMS[key]) url.searchParams.delete(ORJN_SIMPLE_FACET_PARAMS[key]);
      else if (key === 'model') TYPE_PARAMS.forEach((tp) => url.searchParams.delete(tp));
      else if (key === 'sizeprice') { url.searchParams.delete('filter.p.tag'); url.searchParams.delete('filter.v.price.gte'); url.searchParams.delete('filter.v.price.lte'); }
      url.searchParams.delete('page'); history.replaceState({}, '', url); orjnReRenderGrid();
    }));
    bar.querySelectorAll('.orjn-cf__done').forEach((btn) => btn.addEventListener('click', () => closeAll(null)));
    // "Clear all" (desktop bar) — matches basketball: wipes every filter but keeps the gender lock + sort.
    bar.querySelectorAll('[data-orjn-cf-clearall]').forEach((btn) => btn.addEventListener('click', () => {
      const u = new URL(location.href);
      [...u.searchParams.keys()].forEach((k) => { if (k.indexOf('filter.') === 0 && k !== 'filter.p.m.custom.gender' && k !== 'filter.p.m.custom.clothing_type') u.searchParams.delete(k); });
      u.searchParams.delete('page'); u.searchParams.delete('orjn_group_page');
      history.replaceState({}, '', u); orjnReRenderGrid();
    }));
  };

  // ---- Mobile drawer (the theme hides the horizontal facets on phones + shows a "Filters" button) ----
  const orjnCloseDrawer = () => { const d = document.getElementById('orjn-cdrawer'); if (d) { d.hidden = true; document.documentElement.style.overflow = ''; } };
  const orjnOpenDrawer = () => { const d = document.getElementById('orjn-cdrawer'); if (d) { orjnSyncFilterBar(); d.hidden = false; document.documentElement.style.overflow = 'hidden'; } };
  const orjnHookDrawerTrigger = () => {
    if (document.querySelector('.orjn-cf-mobtrigger')) return;
    // Hide the theme's own (empty) mobile filter toggle and drop in our own that opens OUR drawer.
    // Prefer the theme's stable hooks. A cold filtered URL changes the visible copy to e.g. "Filters 1",
    // so relying only on exact text made the replacement trigger disappear on bookmarked filter links.
    const native = document.querySelector('.facets-toggle__button, [data-orjn-open-filters]')
      || [...document.querySelectorAll('button, [role="button"], summary, a')].find((el) =>
        /^\s*filters?(?:\s+\d+)?\s*$/i.test((el.textContent || '').trim()) || /filter/i.test(el.getAttribute('aria-label') || ''));
    if (!native || !native.parentElement) return;
    native.classList.add('orjn-cf-nativefilter');
    const btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'orjn-cf-mobtrigger';
    btn.innerHTML = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M1.5 3.5h13M4 8h8M6.5 12.5h3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg><span>Filters</span><span class="orjn-cf-mobtrigger__n" hidden>0</span>';
    btn.addEventListener('click', (e) => { e.preventDefault(); orjnOpenDrawer(); });
    native.parentElement.insertBefore(btn, native);
  };
  const orjnRenderMobileDrawer = (catalog) => {
    if (document.getElementById('orjn-cdrawer')) { orjnHookDrawerTrigger(); return; }
    const secs = orjnFilterSections(catalog);
    const html = '<div class="orjn-cdrawer" id="orjn-cdrawer" data-orjn-cfilters hidden>' +
      '<div class="orjn-cdrawer__scrim" data-orjn-close></div>' +
      '<div class="orjn-cdrawer__sheet">' +
      '<div class="orjn-cdrawer__head"><span>Filter</span><button type="button" class="orjn-cdrawer__x" data-orjn-close aria-label="Close">×</button></div>' +
      '<div class="orjn-cdrawer__body">' + secs.map((s) =>
        '<div class="orjn-cf" data-facet="' + s.key + '"><button type="button" class="orjn-cf__summary" aria-expanded="false">' + orjnEsc(s.label) +
        '<span class="orjn-cf__badge" hidden>0</span><svg class="orjn-cf__caret" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<div class="orjn-cf__panel" data-model-param="' + orjnEsc(s.modelParam || '') + '">' + s.panel + '</div></div>').join('') +
      '</div>' +
      '<div class="orjn-cdrawer__foot"><button type="button" class="orjn-cf__clear" data-orjn-clearall>Clear all</button><button type="button" class="orjn-cdrawer__apply" data-orjn-close>Done</button></div>' +
      '</div></div>';
    document.body.insertAdjacentHTML('beforeend', html);
    const drawer = document.getElementById('orjn-cdrawer');
    orjnWireFilterBar(catalog, drawer, true);
    drawer.querySelectorAll('[data-orjn-close]').forEach((el) => el.addEventListener('click', orjnCloseDrawer));
    drawer.querySelector('[data-orjn-clearall]').addEventListener('click', () => {
      const u = new URL(location.href); [...u.searchParams.keys()].forEach((k) => { if (k.indexOf('filter.') === 0 && k !== 'filter.p.m.custom.gender' && k !== 'filter.p.m.custom.clothing_type') u.searchParams.delete(k); }); // keep the gender lock
      u.searchParams.delete('page'); u.searchParams.delete('orjn_group_page'); history.replaceState({}, '', u); orjnReRenderGrid();
    });
    orjnHookDrawerTrigger();
  };

  // ---- "Prices vary by size" banner (identical to the native small-collection banner) ----
  // Native filters get this from filters.liquid + orjn-size-price-filter.js. ORJN-managed grids build their
  // own banner with the same CSS classes and separate data-hooks, so the native script never touches it.
  let orjnPriceNoteWired = false;
  const orjnSyncPriceNote = () => {
    const labels = orjnSelectedSizeLabels();
    const hasSize = labels.length > 0;
    const copy = labels.length === 1
      ? 'Size ' + labels[0] + ' applied — prices shown for your size'
      : 'Sizes ' + labels.join(', ') + ' applied — lowest matching price shown';
    document.querySelectorAll('[data-orjn-cf-note]').forEach((n) => { n.hidden = hasSize || !orjnHasSizeChoice(orjnGridCatalogCache || []); });
    document.querySelectorAll('[data-orjn-cf-applied]').forEach((s) => { s.hidden = !hasSize; const c = s.querySelector('[data-orjn-cf-applied-copy]'); if (c) c.textContent = hasSize ? copy : ''; });
  };
  const orjnMountPriceNote = () => {
    if (!document.querySelector('[data-orjn-cf-note]')) {
      const mk = (suffix) => {
        const frag = document.createElement('div');
        frag.innerHTML =
          '<div class="orjn-exact-price-note orjn-exact-price-note--' + suffix + '" data-orjn-cf-note hidden>' +
            '<button type="button" class="orjn-exact-price-note__button" data-orjn-cf-open>' +
              '<span class="orjn-exact-price-note__mark" aria-hidden="true">◆</span>' +
              '<span>Prices vary by size.</span>' +
              '<span class="orjn-exact-price-note__action">' + (suffix === 'mobile' ? 'Select size&nbsp;→' : 'Select your size for exact pricing&nbsp;→') + '</span>' +
            '</button>' +
          '</div>' +
          '<div class="orjn-size-applied orjn-size-applied--' + suffix + '" data-orjn-cf-applied role="status" hidden>' +
            '<div class="orjn-size-applied__inner">' +
              '<span class="orjn-size-applied__mark" aria-hidden="true">✓</span>' +
              '<span class="orjn-size-applied__copy" data-orjn-cf-applied-copy></span>' +
              '<span class="orjn-size-applied__actions">' +
                '<button type="button" class="orjn-size-applied__action" data-orjn-cf-open>Change size&nbsp;→</button>' +
                '<button type="button" class="orjn-size-applied__remove" data-orjn-cf-remove>× Remove</button>' +
              '</span>' +
            '</div>' +
          '</div>';
        return [...frag.children];
      };
      const place = (els, suffix) => {
        // Sit exactly where the native (hidden) banner would — right before it — so placement matches basketball.
        const anchor = document.querySelector('.orjn-exact-price-note--' + suffix + ':not([data-orjn-cf-note])');
        if (anchor && anchor.parentElement) { els.forEach((el) => anchor.parentElement.insertBefore(el, anchor)); return true; }
        const facets = document.querySelector('.facets');
        if (facets && facets.parentElement) { els.forEach((el) => facets.parentElement.insertBefore(el, facets.nextSibling)); return true; }
        return false;
      };
      place(mk('desktop'), 'desktop');
      place(mk('mobile'), 'mobile');
    }
    if (!orjnPriceNoteWired) {
      orjnPriceNoteWired = true;
      document.addEventListener('click', (e) => {
        if (!e.target.closest) return;
        if (e.target.closest('[data-orjn-cf-open]')) { // open the Size & Price picker (desktop dropdown or mobile drawer)
          e.preventDefault();
          if (window.matchMedia('(max-width:749px)').matches) {
            orjnOpenDrawer();
            const sec = document.querySelector('#orjn-cdrawer .orjn-cf[data-facet="sizeprice"]');
            if (sec && !sec.classList.contains('is-open')) { const s = sec.querySelector('.orjn-cf__summary'); if (s) s.click(); }
          } else {
            const cf = document.querySelector('[data-orjn-cfilters]:not(.orjn-cdrawer) .orjn-cf[data-facet="sizeprice"]');
            if (cf) { if (!cf.classList.contains('is-open')) { const s = cf.querySelector('.orjn-cf__summary'); if (s) s.click(); } cf.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
          }
          return;
        }
        if (e.target.closest('[data-orjn-cf-remove]')) { // clear the selected size(s) + the (now size-dependent) price range
          e.preventDefault();
          const url = new URL(location.href);
          url.searchParams.delete('filter.p.tag'); url.searchParams.delete('filter.v.price.gte'); url.searchParams.delete('filter.v.price.lte');
          url.searchParams.delete('page'); url.searchParams.delete('orjn_group_page');
          history.replaceState({}, '', url); orjnReRenderGrid();
        }
      });
    }
    orjnSyncPriceNote();
  };

  const orjnInjectFilterCSS = () => {
    if (document.getElementById('orjn-cfilters-css')) return;
    // Visual tokens replicate the native ORJN filters (price-filter.liquid + orjn-product-groups.css) so the
    // client-side bar is pixel-identical across collections: light-grey labels, square
    // #0a0a0a popovers with a subtle line, lime (#c6ff2e/#b8ff1f) only on active states + the Done bar.
    const FF = 'Inter,-apple-system,BlinkMacSystemFont,sans-serif';
    const css = `
    html.orjn-client-filters .facets__filters-wrapper,
    html.orjn-client-filters .facets-horizontal-remove,
    html.orjn-client-filters .facets-remove--orjn{display:none !important;}
    html.orjn-client-filters .orjn-exact-price-note:not([data-orjn-cf-note]),
    html.orjn-client-filters .orjn-size-applied:not([data-orjn-cf-applied]){display:none !important;}
    @media (min-width:750px){.facets:has([data-orjn-cfilters]:not(.orjn-cdrawer)),.facets__form-wrapper:has([data-orjn-cfilters]:not(.orjn-cdrawer)),.facets-block-wrapper:has([data-orjn-cfilters]:not(.orjn-cdrawer)){flex:1 1 100% !important;width:100% !important;max-width:100% !important;}}
    .orjn-cfilters{display:flex;flex-wrap:wrap;align-items:center;gap:0 26px;width:100%;font-family:${FF};}
    .orjn-cf{position:relative;}
    .orjn-cf__badge[hidden]{display:none !important;}
    .orjn-cf__summary{display:inline-flex;align-items:center;gap:10px;min-height:54px;padding:14px 0;background:none;border:0;color:#f5f5f5;font-family:inherit;font-size:11px;font-weight:700;line-height:1;text-transform:uppercase;letter-spacing:.14em;cursor:pointer;}
    .orjn-cf__summary:hover{color:#fff;}
    .orjn-cf__caret{width:12px;height:12px;color:rgba(245,245,245,.42);transition:transform .15s,color .15s;}
    .orjn-cf.is-open .orjn-cf__caret{transform:rotate(180deg);color:#f5f5f5;}
    .orjn-cf__badge{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 6px;border-radius:999px;background:rgba(198,255,46,.12);color:#c6ff2e;font-size:10px;font-weight:700;}
    .orjn-cf-clearall{display:inline-flex;align-items:center;height:42px;padding:0 16px;margin-left:2px;border:0;border-left:1px solid rgba(245,245,245,.16);background:none;color:rgba(245,245,245,.7);font-family:inherit;font-size:10px;font-weight:700;line-height:1;letter-spacing:.14em;text-transform:uppercase;text-decoration:underline;text-underline-offset:2px;cursor:pointer;transition:color .16s;}
    .orjn-cf-clearall:hover{color:#c6ff2e;}
    .orjn-cf-clearall[hidden]{display:none !important;}
    .orjn-cf__panel{position:absolute;top:calc(100% - 1px);left:0;z-index:40;width:320px;max-height:min(85vh,520px);overflow-y:auto;background:#0a0a0a;border:1px solid rgba(245,245,245,.12);border-radius:0;box-shadow:0 18px 48px 0 rgba(0,0,0,.34);padding:14px 16px 16px;display:none;scrollbar-width:thin;scrollbar-color:rgba(184,255,31,.6) rgba(255,255,255,.06);}
    .orjn-cf[data-facet="sizeprice"] .orjn-cf__panel{width:360px;}
    .orjn-cf__opt[hidden]{display:none !important;}
    .orjn-cf__panel::-webkit-scrollbar{width:6px;}.orjn-cf__panel::-webkit-scrollbar-thumb{background:rgba(184,255,31,.45);border-radius:3px;}.orjn-cf__panel::-webkit-scrollbar-track{background:rgba(255,255,255,.05);}
    .orjn-cf.is-open .orjn-cf__panel{display:block;}
    .orjn-cf__list{display:flex;flex-direction:column;gap:10px;}
    .orjn-cf__opt{display:flex;align-items:center;justify-content:space-between;gap:12px;min-height:44px;padding:11px 12px;border:1px solid rgba(245,245,245,.1);border-radius:0;background:#101010;cursor:pointer;transition:border-color .12s,background .12s;}
    .orjn-cf__opt:hover{border-color:rgba(184,255,31,.5);background:#141414;}
    .orjn-cf__opt input{position:absolute;opacity:0;pointer-events:none;}
    .orjn-cf__box{width:18px;height:18px;flex:0 0 auto;order:0;border:1px solid rgba(245,245,245,.4);border-radius:0;background:transparent;position:relative;transition:background .12s,border-color .12s;}
    .orjn-cf__opt:hover .orjn-cf__box{border-color:rgba(184,255,31,.6);}
    .orjn-cf__opt.is-checked{border-color:rgba(198,255,46,.5);}
    .orjn-cf__opt.is-checked .orjn-cf__box{background:#c6ff2e;border-color:#c6ff2e;}
    .orjn-cf__opt.is-checked .orjn-cf__box:after{content:'';position:absolute;left:5px;top:1px;width:5px;height:9px;border:solid #0a0a0a;border-width:0 2px 2px 0;transform:rotate(45deg);}
    .orjn-cf__name{flex:1;min-width:0;order:1;text-align:right;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;font-weight:600;line-height:1;letter-spacing:.04em;text-transform:uppercase;color:#d9d9d9;}
    .orjn-cf__opt:hover .orjn-cf__name{color:#f5f5f5;}
    .orjn-cf__count{display:none;}
    .orjn-cf[data-facet="sport"] .orjn-cf__count{display:block;order:2;flex:0 0 auto;min-width:2ch;text-align:right;font-size:13px;line-height:1.3;font-weight:500;font-variant-numeric:tabular-nums;color:#aaa;}
    .orjn-cf[data-facet="sport"] .orjn-cf__name{flex:1;text-align:left;}
    .orjn-cf__opt--zero{opacity:.3;}
    .orjn-cf__opt--zero .orjn-cf__name{text-decoration:line-through;}
    .orjn-cf__clear[hidden]{display:none !important;}
    .orjn-cf__empty{opacity:.5;font-size:12px;padding:8px 2px;}
    .orjn-cf--price-only .orjn-cf__sizes,.orjn-cf--price-only .orjn-cf__sp-head:not(.orjn-cf__sp-head--price){display:none!important;}
    .orjn-cf--price-only .orjn-cf__sp-head--price{margin-top:0;padding-top:0;border-top:0;}
    .orjn-cf__sp-head{display:flex;align-items:baseline;gap:8px;margin:0 0 8px;font-size:10px;font-weight:700;line-height:1.3;letter-spacing:.06em;text-transform:uppercase;color:#b8ff1f;}
    .orjn-cf__sp-head--price{margin-top:16px;padding-top:14px;border-top:1px solid rgba(245,245,245,.12);color:rgba(245,245,245,.55);}
    .orjn-cf__sp-hint{font-weight:600;letter-spacing:.04em;text-transform:none;color:rgba(245,245,245,.5);}
    .orjn-cf__sizes{display:grid;grid-template-columns:repeat(auto-fill,minmax(46px,1fr));gap:5px;max-height:188px;overflow-y:auto;overscroll-behavior:contain;padding-right:2px;}
    .orjn-cf__size{display:inline-flex;align-items:center;justify-content:center;height:33px;padding:0 6px;background:rgba(255,255,255,.05);color:#f5f5f5;border:1px solid rgba(245,245,245,.24);border-radius:0;font-family:inherit;font-size:11px;font-weight:800;letter-spacing:.01em;white-space:nowrap;cursor:pointer;transition:border-color .12s,background .12s,color .12s;}
    .orjn-cf__size:hover{border-color:#b8ff1f;background:rgba(184,255,31,.1);}
    .orjn-cf__size.is-on{background:#b8ff1f;border-color:#b8ff1f;color:#0a0a0a;}
    .orjn-cf__size[hidden]{display:none !important;}
    .orjn-cf__price{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:10px;}
    .orjn-cf__price>span{color:rgba(245,245,245,.28);font-size:12px;font-weight:700;text-align:center;}
    .orjn-cf__price-in{width:100%;min-height:46px;padding:0 12px;border:1px solid rgba(245,245,245,.14);border-radius:0;background:#101010;color:#f5f5f5;font-family:inherit;font-size:14px;font-weight:600;text-align:right;}
    .orjn-cf__price-in::placeholder{color:rgba(245,245,245,.32);}
    .orjn-cf__price-in:hover{border-color:rgba(198,255,46,.4);background:#141414;}
    .orjn-cf__price-in:focus{outline:none;border-color:#c6ff2e;}
    .orjn-cf__foot{position:sticky;bottom:-16px;display:flex;gap:10px;align-items:center;margin:14px -16px -16px;padding:14px 16px 16px;background:#0a0a0a;border-top:1px solid rgba(245,245,245,.14);}
    .orjn-cf__clear{min-height:38px;padding:0 12px;border:1px solid rgba(245,245,245,.14);border-radius:0;background:transparent;color:#f5f5f5;font-family:inherit;font-size:10px;font-weight:700;line-height:1;letter-spacing:.14em;text-transform:uppercase;cursor:pointer;transition:border-color .16s,color .16s,background .16s;}
    .orjn-cf__clear:hover{border-color:rgba(198,255,46,.4);color:#c6ff2e;background:#101010;}
    .orjn-cf__done{flex:1 1 auto;min-height:46px;padding:0 18px;border:1px solid #c6ff2e;border-radius:0;background:#c6ff2e;color:#050505;font-family:inherit;font-size:12px;font-weight:900;line-height:1;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;transition:background .16s,border-color .16s,color .16s;}
    .orjn-cf__done:hover{border-color:#f5f5f5;background:#f5f5f5;color:#050505;}
    @media (max-width:749px){ .facets [data-orjn-cfilters]:not(.orjn-cdrawer){display:none !important;} }
    .orjn-cdrawer{position:fixed;inset:0;z-index:9999;font-family:${FF};}
    .orjn-cdrawer[hidden]{display:none;}
    .orjn-cdrawer__scrim{position:absolute;inset:0;background:rgba(0,0,0,.15);-webkit-backdrop-filter:brightness(.75);backdrop-filter:brightness(.75);}
    .orjn-cdrawer__sheet{position:absolute;top:0;bottom:0;left:0;right:auto;width:95%;max-width:500px;height:100%;max-height:none;display:flex;flex-direction:column;background:linear-gradient(180deg,#121212 0%,#0a0a0a 100%);border-top:0;border-left:1px solid rgba(245,245,245,.1);box-shadow:0 18px 50px rgba(0,0,0,.32);border-radius:0;color:#f5f5f5;animation:orjn-cdrawer-in .125s ease-in-out;}
    @keyframes orjn-cdrawer-in{from{transform:translateX(-100%);}to{transform:translateX(0);}}
    @media (prefers-reduced-motion:reduce){.orjn-cdrawer__sheet{animation:none;}}
    .orjn-cdrawer__head{display:flex;align-items:center;justify-content:space-between;padding:12px 10px 12px 18px;font-family:Jost,sans-serif;font-size:32px;font-weight:600;line-height:1.1;text-transform:none;letter-spacing:normal;border-bottom:1px solid rgba(245,245,245,.1);}
    .orjn-cdrawer__x{display:flex;align-items:center;justify-content:center;width:44px;height:44px;flex:none;border-radius:50%;background:none;border:0;color:inherit;font-family:Inter,sans-serif;font-size:24px;font-weight:300;line-height:1;cursor:pointer;padding:0;}
    .orjn-cdrawer__x:hover{opacity:.8;}
    .orjn-cdrawer__body{flex:1;min-height:0;overflow-y:auto;overflow-x:hidden;padding:0 18px;}
    .orjn-cdrawer .orjn-cf{position:static;border-bottom:1px solid rgba(245,245,245,.1);}
    .orjn-cdrawer .orjn-cf__summary{width:100%;justify-content:flex-start;gap:10px;padding:16px 0;font-size:12px;}
    .orjn-cdrawer .orjn-cf__caret{margin-left:auto;}
    .orjn-cdrawer .orjn-cf__panel{position:static;display:none;border:0;background:none;box-shadow:none;padding:0 0 16px;width:auto !important;min-width:0;max-width:none;max-height:none;overflow:visible;}
    .orjn-cdrawer .orjn-cf.is-open .orjn-cf__panel{display:block;}
    .orjn-cdrawer .orjn-cf__foot{display:none;}
    .orjn-cdrawer__foot{position:sticky;bottom:0;display:flex;gap:11px;align-items:stretch;padding:12px 18px 16px;border-top:1px solid rgba(245,245,245,.1);background:linear-gradient(to top,rgba(10,10,10,.98),rgba(10,10,10,.94),rgba(10,10,10,.72),transparent) #0a0a0a;}
    .orjn-cdrawer__foot .orjn-cf__clear{min-height:52px;min-width:120px;}
    .orjn-cdrawer__apply{flex:1;min-width:55%;min-height:52px;background:#c6ff2e;color:#050505;border:1px solid #c6ff2e;border-radius:0;padding:14px;font-family:inherit;font-weight:900;text-transform:uppercase;letter-spacing:.18em;font-size:16px;line-height:1;cursor:pointer;}
    .orjn-cdrawer__apply:hover{background:#f5f5f5;border-color:#f5f5f5;}
    .orjn-cf-mobtrigger{display:none;align-items:center;gap:8px;background:none;border:0;color:#f5f5f5;font-family:${FF};font-weight:700;text-transform:uppercase;letter-spacing:.14em;font-size:11px;cursor:pointer;padding:8px 2px;}
    .orjn-cf-mobtrigger__n{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 6px;border-radius:999px;background:rgba(198,255,46,.12);color:#c6ff2e;font-size:10px;font-weight:700;}
    .orjn-cf-mobtrigger__n[hidden]{display:none;}
    @media(max-width:749px){ .orjn-cf-mobtrigger{display:inline-flex;} .orjn-cf-nativefilter{display:none !important;} }
    `;
    const style = document.createElement('style'); style.id = 'orjn-cfilters-css'; style.textContent = css; document.head.appendChild(style);
  };

  const mountGrid = async () => {
    const mountStartedAt = performance.now();
    const mountTiming = { startedAt: mountStartedAt, url: location.pathname + location.search };
    if (!enabled() || location.pathname.startsWith('/products/')) return;
    let nativeGrid = document.querySelector('.main-collection-grid .product-grid:not(.orjn-groups-grid), results-list .product-grid:not(.orjn-groups-grid)');
    // A valid client-side filter can be broader than Shopify's exact server filter (notably size aliases:
    // 44 means 44 + 44 1/3). When Shopify's exact value returns zero it omits the <ul> entirely, so give
    // the full-catalog engine a hidden anchor to mount beside. Only do this while the early loader is
    // pending, which proves Liquid saw products in the unfiltered collection; true empty routes/searches
    // keep their native empty state.
    if (!nativeGrid && document.documentElement.classList.contains('orjn-groups-pending')
      && !/^\/collections\/football(?:\/|$)/.test(location.pathname)) {
      const host = document.querySelector('.main-collection-grid__inner, results-list');
      if (host) {
        nativeGrid = document.createElement('ul');
        nativeGrid.className = 'product-grid product-grid--grid orjn-synthetic-native-grid';
        nativeGrid.setAttribute('role', 'list');
        nativeGrid.setAttribute('product-grid-view', 'default');
        nativeGrid.style.setProperty('--mobile-columns', '2');
        host.querySelector('.main-collection-grid__empty')?.classList.add('orjn-native-grid-hidden');
        host.appendChild(nativeGrid);
      }
    }
    if (!nativeGrid || nativeGrid.dataset.orjnGroupsSource) return;
    const types = collectTypes();
    if (!types.length) {
      // Football is owner-designated native/ungrouped. Keep it native even on a zero-result filtered URL,
      // where Shopify may omit every facet input and would otherwise resemble a filter-less big collection.
      if (/\/collections\/football(?:\/|$)/.test(location.pathname)) { finishPending(); return; }
      // On a cold filtered URL Shopify can omit every model option because the server-rendered subset has
      // no matching model facets. That must NOT drop back to the native path: the canonical blank feed still
      // contains the complete groupParam/groupValue fields and is the authoritative source for both grouping
      // and filters. With `types` empty the label safely falls back to product.groupValue below. This is also
      // the path used by very large/filter-less collections. The explicit Football exception above is the
      // only collection that stays native by owner request.
    }
    nativeGrid.dataset.orjnGroupsSource = 'loading';
    nativeGrid.classList.add('orjn-native-grid-hidden');
    const nativePagination = nativeGrid.parentElement?.querySelector('.pagination');
    nativePagination?.classList.add('orjn-native-grid-hidden');

    const params = new URLSearchParams(location.search);
    const requestedPage = Math.max(1, Number(params.get('page')) || Number(params.get('orjn_group_page')) || 1);
    // Progressive preview DISABLED on every URL. It rendered one product type cheapest-first, which is
    // not the final order, so a "random" shoe would flash before the real grid replaced it. Owner does
    // not want that anywhere — keep the loading skeleton until the correct grid is ready, then swap once.
    const progressive = Promise.resolve();
    const clientMode = orjnClientFilterMode();
    if (clientMode) orjnMarkCountLoading(); // hide a stale server count while the full cached catalog is narrowed
    await progressive;
    // Keep the parsed feed in memory so client-side filtering re-renders instantly (no re-fetch).
    const rawCatalog = orjnGridCatalogCache || (orjnGridCatalogCache = await loadCatalog());
    if (!orjnGridCatalogSortKey) orjnGridCatalogSortKey = params.get('sort_by') === 'best-selling' ? 'best-selling' : 'canonical';
    mountTiming.feedMs = Math.round(performance.now() - mountStartedAt);
    mountTiming.rawProducts = rawCatalog.length;
    // All ORJN-managed grids now use the same cached, in-browser filtering path that made Lifestyle fast.
    // Native-only collections already returned above, so their controls and loading behaviour stay untouched.
    if (clientMode) {
      const filterUiStartedAt = performance.now();
      orjnInjectFilterCSS();
      document.documentElement.classList.add('orjn-client-filters');
      orjnRenderFilterBar(rawCatalog);
      orjnRenderMobileDrawer(rawCatalog);
      orjnMountPriceNote();
      orjnSyncFilterBar();
      mountTiming.filterUiMs = Math.round(performance.now() - filterUiStartedAt);
    }
    const catalog = clientMode ? orjnApplyFilters(rawCatalog) : orjnFilterProducts(rawCatalog);
    mountTiming.filterApplyMs = Math.max(0, Math.round(performance.now() - mountStartedAt - mountTiming.feedMs - (mountTiming.filterUiMs || 0)));
    mountTiming.filteredProducts = catalog.length;
    if (clientMode && !catalog.length) {
      document.querySelectorAll('.orjn-groups-grid, .orjn-group-pagination').forEach((el) => el.remove());
      const empty = document.createElement('div');
      empty.className = 'orjn-groups-grid orjn-cf-empty-results';
      empty.style.cssText = 'padding:48px 8px;text-align:center;opacity:.7;font-size:15px;';
      empty.textContent = 'No products match these filters.';
      nativeGrid.insertAdjacentElement('afterend', empty);
      nativeGrid.dataset.orjnGroupsSource = 'true';
      finishPending();
      return;
    }
    // loadCatalog preserves Shopify's product order. For sort_by=best-selling, smaller indexes are
    // stronger sellers; groups retain these ranks even though their internal colors are sorted by price.
    const catalogRanks = new Map(catalog.map((product, index) => [String(product.id), index]));
    if (!nativeGrid.isConnected) return;
    if (document.documentElement.classList.contains('orjn-groups-timed-out')) {
      nativeGrid.dataset.orjnGroupsSource = 'timeout';
      nativeGrid.classList.remove('orjn-native-grid-hidden');
      nativePagination?.classList.remove('orjn-native-grid-hidden');
      return;
    }
    const typeLabels = new Map(types.map((type) => [`${type.param}\u0000${type.value}`, type.label]));
    const groupedProducts = new Map();
    const blankProducts = [];
    catalog.forEach((product) => {
      // Kids are NOT grouped yet (owner: grouping for kids comes later) — any product with an age_range
      // renders as its own individual card, never merged into an adult model group.
      if (IS_LATEST_DROPS() || !TYPE_PARAMS.includes(product.groupParam) || !product.groupValue || product.ageRange) {
        blankProducts.push(product);
        return;
      }
      const typeKey = `${product.groupParam}\u0000${product.groupValue}`;
      if (!groupedProducts.has(typeKey)) {
        groupedProducts.set(typeKey, {
          type: {
            param: product.groupParam,
            value: product.groupValue,
            label: typeLabels.get(typeKey) || product.groupValue,
          },
          normal: [],
          sale: [],
        });
      }
      groupedProducts.get(typeKey)[isSaleProduct(product) ? 'sale' : 'normal'].push(product);
    });
    mountTiming.groupingMs = Math.round(performance.now() - mountStartedAt - mountTiming.feedMs - (mountTiming.filterUiMs || 0) - mountTiming.filterApplyMs);

    // Latest Drops (new-in) orders its group cards NEWEST-first — each group's most recent colorway
    // (max createdAt) leads — so the freshest drops sit at the top. Every other collection keeps the
    // default order (by type param, then model name alphabetically).
    const latestDrops = IS_LATEST_DROPS();
    // Latest Drops: the newest N products (grouped OR ungrouped) break out as their own individual cards,
    // so a brand-new colorway of a big model (e.g. a fresh Air Force 1) shows on its own instead of hiding
    // inside that model's group. The model's remaining (older) colorways still collapse into one group card.
    const LATEST_DROPS_LEAD = 12;
    const leadIndividuals = [];
    if (latestDrops) {
      const all = [];
      groupedProducts.forEach((entry) => { entry.normal.forEach((p) => all.push(p)); entry.sale.forEach((p) => all.push(p)); });
      blankProducts.forEach((p) => all.push(p));
      all.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
      const leadIds = new Set(all.slice(0, LATEST_DROPS_LEAD).map((p) => String(p.id)));
      groupedProducts.forEach((entry) => {
        ['normal', 'sale'].forEach((bucket) => {
          const keep = [];
          entry[bucket].forEach((p) => { if (leadIds.has(String(p.id))) leadIndividuals.push(p); else keep.push(p); });
          entry[bucket] = keep;
        });
      });
      Array.from(groupedProducts.keys()).forEach((key) => { const e = groupedProducts.get(key); if (!e.normal.length && !e.sale.length) groupedProducts.delete(key); });
    }
    const entryNewest = (entry) => {
      let newest = 0;
      entry.normal.concat(entry.sale).forEach((product) => { const c = Number(product.createdAt || 0); if (c > newest) newest = c; });
      return newest;
    };
    const typeEntries = Array.from(groupedProducts.values()).sort((a, b) => {
      if (latestDrops) return entryNewest(b) - entryNewest(a) || a.type.label.localeCompare(b.type.label, undefined, { numeric: true, sensitivity: 'base' });
      const field = TYPE_PARAMS.indexOf(a.type.param) - TYPE_PARAMS.indexOf(b.type.param);
      return field || a.type.label.localeCompare(b.type.label, undefined, { numeric: true, sensitivity: 'base' });
    });
    const groups = [];
    typeEntries.forEach((entry) => {
      ['normal', 'sale'].forEach((bucket) => {
        const products = entry[bucket].sort(orjnByPriceAsc);
        productBatches(products).forEach((batch, index) => {
          groups.push({
            type: entry.type,
            size: batch.length,
            page: index + 1,
            sale: bucket === 'sale',
            products: batch,
          });
        });
      });
    });
    // On Latest Drops, any ungrouped (blank-subtype) products also lead newest-first. (Grouped cards
    // still come before ungrouped ones in the packed order; within each, newest is first.)
    if (latestDrops) blankProducts.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
    if (!groups.length && !leadIndividuals.length && !blankProducts.length) {
      nativeGrid.dataset.orjnGroupsSource = 'empty';
      nativeGrid.classList.remove('orjn-native-grid-hidden');
      nativePagination?.classList.remove('orjn-native-grid-hidden');
      finishPending();
      return;
    }

    const progressiveGrid = document.querySelector('.orjn-groups-grid--progressive');
    document.querySelectorAll('.orjn-groups-grid:not(.orjn-groups-grid--progressive), .orjn-group-pagination').forEach((element) => element.remove());
    nativeGrid.dataset.orjnGroupsSource = 'true';

    // Packed item order. Default: all group cards first, then ungrouped/individual cards. Latest Drops:
    // interleave both by date (newest first) so the freshest pairs lead whether or not they're grouped —
    // otherwise a brand-new ungrouped product would sit behind older groups (blanks used to come last).
    const groupNewest = (g) => { let n = 0; g.products.forEach((p) => { const c = Number(p.createdAt || 0); if (c > n) n = c; }); return n; };
    const groupItems = groups.map((g) => ({ kind: 'group', group: g }));
    const individualItems = blankProducts.concat(leadIndividuals).map((p) => ({ kind: 'blank', product: p }));
    const priceSort = params.get('sort_by');
    const priceDirection = priceSort === 'price-ascending' ? 1 : priceSort === 'price-descending' ? -1 : 0;
    const bestSelling = priceSort === 'best-selling';
    const dateDirection = priceSort === 'created-ascending' ? 1 : priceSort === 'created-descending' ? -1 : 0;
    const itemStartPrice = (item) => item.kind === 'group'
      ? orjnSortPrice(item.group.products[0])
      : orjnSortPrice(item.product);
    const itemLabel = (item) => item.kind === 'group'
      ? item.group.type.label
      : (item.product.title || '');
    const productRank = (product) => catalogRanks.get(String(product.id)) ?? Number.MAX_SAFE_INTEGER;
    const itemBestRank = (item) => item.kind === 'group'
      ? item.group.products.reduce((best, product) => Math.min(best, productRank(product)), Number.MAX_SAFE_INTEGER)
      : productRank(item.product);
    const itemDate = (item) => {
      if (item.kind === 'blank') return Number(item.product.createdAt || 0);
      const dates = item.group.products.map((product) => Number(product.createdAt || 0)).filter((date) => date > 0);
      if (!dates.length) return dateDirection > 0 ? Number.MAX_SAFE_INTEGER : 0;
      return dateDirection > 0 ? Math.min(...dates) : Math.max(...dates);
    };
    let orderedItems;
    if (priceDirection) {
      // Global price order across group and individual cards. A group's key is its first/cheapest
      // qualifying colorway, while the colorways inside the group remain cheapest-first.
      orderedItems = groupItems.concat(individualItems).sort((a, b) => {
        const priceOrder = (itemStartPrice(a) - itemStartPrice(b)) * priceDirection;
        return priceOrder || itemLabel(a).localeCompare(itemLabel(b), undefined, { numeric: true, sensitivity: 'base' });
      });
    } else if (bestSelling) {
      // Shopify ranks individual colorways. A displayed group's best-ranked colorway supplies its rank;
      // groups and individuals share one global order while each group's colors remain cheapest-first.
      orderedItems = groupItems.concat(individualItems).sort((a, b) =>
        (itemBestRank(a) - itemBestRank(b))
        || itemLabel(a).localeCompare(itemLabel(b), undefined, { numeric: true, sensitivity: 'base' }));
    } else if (dateDirection) {
      // Newâ†’old ranks a group by its newest colorway; oldâ†’new ranks it by its oldest. Groups and
      // individuals share one global order, while colorways inside every group remain cheapest-first.
      orderedItems = groupItems.concat(individualItems).sort((a, b) =>
        ((itemDate(a) - itemDate(b)) * dateDirection)
        || itemLabel(a).localeCompare(itemLabel(b), undefined, { numeric: true, sensitivity: 'base' }));
    } else if (latestDrops) {
      orderedItems = groupItems.map((item) => ({ ...item, ts: groupNewest(item.group) }))
        .concat(individualItems.map((item) => ({ ...item, ts: Number(item.product.createdAt || 0) })))
        .sort((a, b) => b.ts - a.ts);
    } else {
      orderedItems = groupItems.concat(individualItems);
    }

    const totalItems = orderedItems.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / GRID_PAGE_SIZE));
    const currentPage = Math.min(requestedPage, totalPages);
    const start = (currentPage - 1) * GRID_PAGE_SIZE;
    const end = Math.min(start + GRID_PAGE_SIZE, totalItems);
    const visibleItems = orderedItems.slice(start, end);

    const grid = progressiveGrid || document.createElement('ul');
    grid.className = `${nativeGrid.className.replace('orjn-native-grid-hidden', '')} orjn-groups-grid`;
    grid.setAttribute('role', 'list');
    grid.setAttribute('product-grid-view', nativeGrid.getAttribute('product-grid-view') || 'default');
    grid.style.cssText = nativeGrid.style.cssText;
    const previewCards = progressiveGrid ? Array.from(progressiveGrid.querySelectorAll('.orjn-group-card')) : [];
    const visibleGroups = {}; // card index -> group object (group cards only), for lazy hydration
    const orderedCards = visibleItems.map((item, index) => {
      if (item.kind === 'group') {
        visibleGroups[index] = item.group;
        const existing = previewCards.find((card) => card.dataset.groupKey === groupKey(item.group));
        if (existing) { existing.dataset.groupIndex = String(index); return existing; }
        return cardMarkup(item.group, index);
      }
      return individualCardMarkup(item.product, index);
    });
    grid.replaceChildren(...orderedCards);
    if (!grid.isConnected) nativeGrid.insertAdjacentElement('afterend', grid);
    const nav = pagination(currentPage, totalPages);
    if (nav) grid.insertAdjacentElement('afterend', nav);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        const group = visibleGroups[Number(entry.target.dataset.groupIndex)];
        if (group) hydrateCard(entry.target, group);
      });
    }, { rootMargin: '700px 0px' });
    // Only group cards need lazy hydration (individual cards handle their own quick-add).
    const groupCards = orderedCards.filter((card) => card.classList && card.classList.contains('orjn-group-card'));
    const initialCardCount = window.matchMedia('(max-width:749px)').matches ? 4 : 6;
    const initialCards = groupCards.slice(0, initialCardCount);
    const deferredCards = groupCards.slice(initialCardCount);
    // Reveal the built grid IMMEDIATELY — do NOT wait for the first cards' images to download+decode. On a
    // slow connection (e.g. Lebanon wifi) that wait kept a full-card skeleton on screen for seconds; now the
    // customer sees the real cards at once (titles, colour counts, layout), with prices/thumbnails filling in
    // and the main images streaming in over their shimmering tiles. Hydration still runs — just not awaited
    // before the reveal — and the above-fold cards (first 6) hydrate now so their images start downloading
    // right away; the rest hydrate as they scroll near the viewport (observer, 700px margin).
    finishPending();
    mountTiming.totalMs = Math.round(performance.now() - mountStartedAt);
    mountTiming.cards = orderedCards.length;
    window.__orjnLastMountTiming = mountTiming;
    initialCards.forEach((card) => {
      const group = visibleGroups[Number(card.dataset.groupIndex)];
      if (group) hydrateCard(card, group);
    });
    deferredCards.forEach((card) => observer.observe(card));
  };

  const mountProduct = async () => {
    if (!enabled() || !location.pathname.startsWith('/products/')) return;
    const contextElement = document.getElementById('OrjnGroupContext');
    if (!contextElement) return;
    let context;
    try { context = JSON.parse(contextElement.textContent); } catch { return; }
    const params = new URLSearchParams(location.search);
    const groupParam = TYPE_PARAMS.includes(params.get('orjn_group_param')) ? params.get('orjn_group_param') : context.groupParam;
    const groupValue = params.get('orjn_group_value') || context.groupValue;
    const batch = Math.max(1, Number(params.get('orjn_group_batch')) || 1);
    let sale = params.get('orjn_group_sale') === '1';
    if (!groupParam || !groupValue) return;
    // Kids are not grouped yet (owner: kids grouping comes later) — no colorway group on a kids product.
    if (context.ageRange) return;
    // The storefront metafield filter (filter.p.m.custom.<type>) is NOT enabled, so asking Shopify for a
    // filtered feed returns the WHOLE catalog. Instead load the model's broad-type collection blank feed
    // and match this model's ADULT colorways in the browser — the same robust way the collection grid
    // groups — then show the batch that contains this product. (Also lighter than the old catalog pull.)
    const type = { param: groupParam, value: groupValue, label: groupValue };
    const handle = TYPE_COLLECTION[groupParam];
    if (!handle) return;
    const catalog = await loadAllPages((page) => {
      const u = new URL(location.origin + '/collections/' + handle);
      u.searchParams.set('view', 'orjn-blank-data');
      u.searchParams.set('page', String(page));
      return u;
    });
    // Match the current product's sale bucket (grouping keeps sale and normal colorways separate).
    if (params.get('orjn_group_sale') == null) {
      const me = catalog.find((item) => String(item.id) === String(context.id));
      if (me) sale = isSaleProduct(me);
    }
    const bucket = catalog
      .filter((product) => product.groupParam === groupParam && product.groupValue === groupValue && !product.ageRange && isSaleProduct(product) === sale)
      .sort(orjnByPriceAsc);
    const batches = productBatches(bucket);
    let products = batches.find((b) => b.some((item) => String(item.id) === String(context.id)))
      || batches[batch - 1] || batches[0] || [];
    if (!products.some((item) => String(item.id) === String(context.id))) products.unshift(context);
    const title = document.querySelector('.orjn-product__title');
    if (!title || title.parentElement.querySelector('.orjn-product-colors')) return;
    const panel = document.createElement('div');
    panel.className = 'orjn-product-colors';
    panel.innerHTML = `
      <div class="orjn-product-colors__head">
        <span class="orjn-product-colors__title">${esc(groupValue)}${sale ? ' · ON SALE' : ''}</span>
        <span class="orjn-product-colors__count">${products.length} ${products.length === 1 ? 'COLOR' : 'COLORS'}</span>
      </div>
      <div class="orjn-product-colors__rail">
        <button class="orjn-product-colors__arrow orjn-product-colors__arrow--left" type="button" data-orjn-colors-prev aria-label="Previous colours">&#8592;</button>
        <div class="orjn-product-colors__list" data-orjn-colors-list>
          ${products.map((product) => {
            const href = productUrl(product, { type, page: batch, sale });
            const current = String(product.id) === String(context.id);
            return `<a class="orjn-product-colors__link${current ? ' is-current' : ''}" href="${esc(href)}" aria-label="${esc(product.title)}" data-orjn-label="${esc(product.title)}" data-orjn-sizes="${esc(product.sizesAvail || '')}" data-orjn-sizeprices="${esc(product.sizePrices || '')}" data-orjn-money="${esc(product.priceMoney || '')}"${current ? ' aria-current="page"' : ''}>
              <img src="${esc(product.thumb || product.image)}" alt="" loading="lazy" width="76" height="76">
              <span class="orjn-product-colors__price" data-orjn-color-price aria-hidden="true" hidden></span>
            </a>`;
          }).join('')}
        </div>
        <button class="orjn-product-colors__arrow orjn-product-colors__arrow--right" type="button" data-orjn-colors-next aria-label="More colours">&#8594;</button>
      </div>
      <div class="orjn-product-colors__progress" aria-hidden="true"><span data-orjn-colors-progress></span></div>
      <p class="orjn-product-colors__size-note" data-orjn-colors-size-note role="status" hidden>
        <span aria-hidden="true">×</span> Crossed colors are unavailable in your selected size <strong data-orjn-colors-size-note-value></strong>
      </p>`;
    title.insertAdjacentElement('afterend', panel);

    // Branded slider: arrows smooth-scroll exactly one colour at a time, a lime progress line tracks position, the current
    // colourway is centred on load, and the arrows/progress hide (or disable at the ends) when they aren't
    // needed. Native scrollbar is hidden in CSS — this is the on-brand replacement (matches group cards).
    const colorsList = panel.querySelector('[data-orjn-colors-list]');
    const colorsPrev = panel.querySelector('[data-orjn-colors-prev]');
    const colorsNext = panel.querySelector('[data-orjn-colors-next]');
    const colorsBar = panel.querySelector('[data-orjn-colors-progress]');
    const colorsStep = () => {
      const links = colorsList.querySelectorAll('.orjn-product-colors__link');
      if (links.length > 1) return Math.max(1, links[1].offsetLeft - links[0].offsetLeft);
      return Math.max(1, links[0]?.offsetWidth || 1);
    };
    const syncColorsNav = () => {
      const max = colorsList.scrollWidth - colorsList.clientWidth;
      const scrollable = max > 1;
      panel.classList.toggle('is-scrollable', scrollable);
      if (colorsBar) colorsBar.style.width = scrollable ? `${Math.min(1, colorsList.scrollLeft / max) * 100}%` : '0%';
      if (colorsPrev) colorsPrev.disabled = !scrollable || colorsList.scrollLeft <= 1;
      if (colorsNext) colorsNext.disabled = !scrollable || colorsList.scrollLeft >= max - 1;
    };
    if (colorsPrev) colorsPrev.addEventListener('click', () => colorsList.scrollBy({ left: -colorsStep(), behavior: 'smooth' }));
    if (colorsNext) colorsNext.addEventListener('click', () => colorsList.scrollBy({ left: colorsStep(), behavior: 'smooth' }));
    colorsList.addEventListener('scroll', () => window.requestAnimationFrame(syncColorsNav), { passive: true });
    window.addEventListener('resize', () => window.requestAnimationFrame(syncColorsNav));
    const currentColor = colorsList.querySelector('.orjn-product-colors__link.is-current');
    if (currentColor) colorsList.scrollLeft = Math.max(0, currentColor.offsetLeft - (colorsList.clientWidth - currentColor.offsetWidth) / 2);
    syncColorsNav();
    // thumbnails may still be loading — re-measure once they settle so overflow/arrows are accurate
    window.setTimeout(syncColorsNav, 400);
    window.setTimeout(syncColorsNav, 1200);

    // ── Size availability across colorways ──────────────────────────────────
    // When the shopper picks a size in the size grid, dim + cross the colorways that don't have that size
    // in stock (each colorway carries its available sizes in data-orjn-sizes, from the feed's sizesAvail).
    // The values match the size grid's data-size-value (both are the raw variant size option). Clearing
    // the size restores all colorways. The current colorway's picked size is always in stock (selectable),
    // so it never crosses itself.
    const colorLinks = Array.from(colorsList.querySelectorAll('.orjn-product-colors__link'));
    const sizeNote = panel.querySelector('[data-orjn-colors-size-note]');
    const sizeNoteValue = panel.querySelector('[data-orjn-colors-size-note-value]');
    const parseSizes = (value) => new Set(String(value || '').split(';').map((s) => s.trim()).filter(Boolean));
    const applySizeAvailability = (size) => {
      let unavailableCount = 0;
      colorLinks.forEach((link) => {
        if (!link.dataset.orjnHref) link.dataset.orjnHref = link.getAttribute('href') || '';
        const base = link.dataset.orjnHref;
        // Never cross the current colorway: the shopper can only select a size that IS in stock here, and
        // its data comes from OrjnGroupContext (which has no size list), so treat it as always available.
        const carried = filteredSizeAliases();
        const matchingSizes = carried.includes(size) ? carried : [size];
        const availableSizes = parseSizes(link.dataset.orjnSizes);
        const off = Boolean(size) && !link.classList.contains('is-current') && !matchingSizes.some((value) => availableSizes.has(value));
        if (off) unavailableCount += 1;
        link.classList.toggle('orjn-product-colors__link--size-off', off);
        if (off) link.setAttribute('title', `Size ${size} unavailable in this colour`);
        else link.removeAttribute('title');
        // Selected-size price bubble: show what THIS colour costs in the chosen size, so the shopper can
        // compare colours without opening each one. Uses the same alias group as the availability check
        // above (44 and 44 2/3 are one shopper-facing size), and the cheapest match when several alias.
        // Skipped when no size is chosen, and on colours crossed out as unavailable in that size — a price
        // on something you cannot buy would be misleading. sizePrices comes from the same blank-data feed
        // that already drives sizesAvail, so no extra request.
        const priceEl = link.querySelector('[data-orjn-color-price]');
        if (priceEl) {
          let cents = null;
          if (size && !off) {
            const priceMap = orjnParseSizePrices(link.dataset.orjnSizeprices);
            matchingSizes.forEach((value) => {
              const found = priceMap[value];
              if (found != null && (cents == null || found < cents)) cents = found;
            });
          }
          const baseLabel = link.dataset.orjnLabel || link.getAttribute('aria-label') || '';
          if (cents == null) {
            priceEl.hidden = true;
            priceEl.textContent = '';
            link.setAttribute('aria-label', baseLabel);
          } else {
            // trailing ".00" is dropped so "44 $195" fits the 58px swatch; odd cents keep their decimals
            const money = orjnMoney(link.dataset.orjnMoney, cents).replace(/\.00(?=\D|$)/, '');
            // size first (muted) then the price (bold) — the price is what's being compared, the size is
            // the context it applies to, so the hierarchy makes the number scannable across colours
            priceEl.innerHTML = `<span class="orjn-product-colors__price-size">${esc(size)}</span><span class="orjn-product-colors__price-value">${esc(money)}</span>`;
            priceEl.hidden = false;
            // the chip is aria-hidden, so fold the same information into the link's accessible name
            link.setAttribute('aria-label', `${baseLabel}, ${money} in size ${size}`);
          }
        }
        // Carry the chosen size to the other colorway's product page (via ?orjn_size=) so it opens with the
        // same size pre-selected. The target only applies it if that size is in stock there; otherwise it
        // opens unselected. Clearing the size restores the plain link.
        if (size) {
          link.setAttribute('href', base + (base.indexOf('?') >= 0 ? '&' : '?') + 'orjn_size=' + encodeURIComponent(size));
        } else {
          link.setAttribute('href', base);
        }
      });
      if (sizeNoteValue) sizeNoteValue.textContent = size || '';
      if (sizeNote) sizeNote.hidden = !size || unavailableCount === 0;
    };
    const selectedSizeValue = () => {
      const sel = document.querySelector('.orjn-product__size-option.is-selected');
      return sel ? (sel.dataset.sizeValue || '') : '';
    };
    const sizeGrid = document.querySelector('.orjn-product__size-grid');
    if (sizeGrid) {
      const sizeObserver = new MutationObserver(() => applySizeAvailability(selectedSizeValue()));
      sizeObserver.observe(sizeGrid, { attributes: true, attributeFilter: ['class'], subtree: true });
      applySizeAvailability(selectedSizeValue());
    }
  };

  const handleGridError = (error) => {
    console.error('[ORJN groups]', error);
    document.querySelectorAll('.orjn-groups-grid, .orjn-group-pagination').forEach((element) => element.remove());
    document.querySelectorAll('.product-grid[data-orjn-groups-source]').forEach((grid) => {
      grid.dataset.orjnGroupsSource = 'error';
      grid.classList.remove('orjn-native-grid-hidden');
      grid.parentElement?.querySelector('.pagination')?.classList.remove('orjn-native-grid-hidden');
    });
    document.querySelectorAll('.main-collection-grid__empty.orjn-native-grid-hidden').forEach((empty) => empty.classList.remove('orjn-native-grid-hidden'));
    finishPending();
  };

  // Collections without grouping (football, or a collection without a populated grouping metafield) keep
  // the theme's native cards. Decorate those links too, so size carry-over works consistently site-wide.
  const decorateNativeProductLinks = () => {
    if (location.pathname.startsWith('/products/') || !filteredSizeAliases().length) return;
    document.querySelectorAll('.product-grid a[href*="/products/"], product-card a[href*="/products/"]').forEach((link) => {
      const raw = link.getAttribute('href');
      if (!raw) return;
      link.setAttribute('href', productHref(raw));
    });
  };

  const start = () => {
    setupIndividualQuickAdd();
    decorateNativeProductLinks();
    mountGrid().catch(handleGridError);
    mountProduct().catch((error) => console.error('[ORJN product colors]', error));
    const observer = new MutationObserver(() => window.setTimeout(() => {
      decorateNativeProductLinks();
      mountGrid().catch(handleGridError);
    }, 80));
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();


  (() => {
    if (window.ORJNProductCardQuickAddInitializedV5) return;
    window.ORJNProductCardQuickAddInitializedV5 = true;

    const baseUrl = window.Shopify?.routes?.root || '/';

    const drawerSelectors = [
      'cart-drawer',
      '[data-cart-drawer]',
      '.cart-drawer',
      'details[id*="CartDrawer"]',
      'details[id*="cart-drawer"]'
    ];

    const qs = (root, selectors) => {
      for (const selector of selectors) {
        const found = root.querySelector(selector);
        if (found) return found;
      }
      return null;
    };

    const getGallery = (card) => {
      if (!card) return null;
      return qs(card, [
        '.card-gallery',
        '.product-card-gallery',
        '[class*="card-gallery"]'
      ]);
    };

    const getOverlayHost = (card) => {
      if (!card) return null;

      let host = card.querySelector('.orjn-card-overlay-host');
      if (host) return host;

      host = document.createElement('div');
      host.className = 'orjn-card-overlay-host';
      card.appendChild(host);
      return host;
    };

    const syncQuickAddOffsets = (cards) => {
      const measurements = Array.from(new Set(cards)).filter(Boolean).map((card) => {
        const content = card.querySelector('.product-card__content');
        if (!content) return { card, offset: 0 };
        const cardRect = card.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        return { card, offset: Math.max(0, Math.round((contentRect.top - cardRect.top) * 100) / 100) };
      });
      measurements.forEach(({ card, offset }) => {
        if (offset > 0) card.style.setProperty('--orjn-qa-row-offset', `${offset}px`);
        else card.style.removeProperty('--orjn-qa-row-offset');
      });
    };

    const syncAllQuickAddOffsets = (scope = document) => {
      const root = scope instanceof Element ? scope : document;
      syncQuickAddOffsets(root.querySelectorAll?.('.product-card') || []);
    };

    const placeQuickAdd = (scope = document) => {
      if (!(scope instanceof Element) && scope !== document) return;

      const quickAdds = scope.querySelectorAll
        ? scope.querySelectorAll('[data-orjn-qa]:not([data-orjn-qa-mounted])')
        : [];

      const movedCards = [];
      quickAdds.forEach((quickAdd) => {
        const card = quickAdd.closest('.product-card');
        if (!card) return;

        const gallery = getGallery(card);
        if (!gallery) return;

        const host = getOverlayHost(card);
        if (!host) return;

        quickAdd.setAttribute('data-orjn-qa-mounted', 'true');
        host.appendChild(quickAdd);
        movedCards.push(card);
      });

      if (movedCards.length) requestAnimationFrame(() => syncQuickAddOffsets(movedCards));

    };

    const setCardActive = (quickAdd, state) => {
      const card = quickAdd?.closest('.product-card');
      if (!card) return;
      card.classList.toggle('is-qa-active', state);
    };

    const closePanel = (quickAdd) => {
      if (!quickAdd) return;

      quickAdd.classList.remove('is-open');
      setCardActive(quickAdd, false);

      const panel = quickAdd.querySelector('[data-orjn-qa-panel]');
      const trigger = quickAdd.querySelector('[data-orjn-qa-trigger]');

      if (panel) {
        if (panel.matches(':popover-open')) panel.hidePopover();
        panel.removeAttribute('popover');
        panel.hidden = true;
      }
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    };

    const closeAllPanels = (except = null) => {
      document.querySelectorAll('[data-orjn-qa].is-open').forEach((quickAdd) => {
        if (except && quickAdd === except) return;
        closePanel(quickAdd);
      });
    };

    // A compact top-layer popover, anchored to its product button (not a phone sheet).
    const positionCompactPreview = (quickAdd) => {
      const panel = quickAdd.querySelector('[data-orjn-qa-panel]');
      const trigger = quickAdd.querySelector('[data-orjn-qa-trigger]');
      if (!panel || !trigger) return;
      const anchor = trigger.getBoundingClientRect();
      const width = Math.min(268, window.innerWidth - 24);
      const maxHeight = Math.min(300, window.innerHeight - 140);
      const height = Math.min(panel.scrollHeight + 2, maxHeight);
      const left = Math.max(12, Math.min(anchor.right - width, window.innerWidth - width - 12));
      const below = anchor.bottom + 8;
      const above = anchor.top - height - 8;
      const top = below + height <= window.innerHeight - 12 ? below : Math.max(12, above);
      panel.style.setProperty('--qa-left', left + 'px');
      panel.style.setProperty('--qa-top', top + 'px');
    };

    const openPanel = (quickAdd) => {
      if (!quickAdd) return;

      const panel = quickAdd.querySelector('[data-orjn-qa-panel]');
      const trigger = quickAdd.querySelector('[data-orjn-qa-trigger]');
      if (!panel || !trigger) return;

      closeAllPanels(quickAdd);
      quickAdd.classList.add('is-open');
      setCardActive(quickAdd, true);
      panel.hidden = false;
      if (!panel.querySelector('[data-orjn-qa-close]')) {
        const close = document.createElement('button');
        close.type = 'button'; close.className = 'orjn-card-qa__close';
        close.dataset.orjnQaClose = ''; close.setAttribute('aria-label', 'Close size preview'); close.textContent = '×';
        close.addEventListener('click', () => { closePanel(quickAdd); trigger.focus({preventScroll:true}); });
        panel.querySelector('.orjn-card-qa__panel-head')?.appendChild(close);
      }
      if (window.matchMedia('(max-width:749px)').matches && typeof panel.showPopover === 'function') {
        panel.setAttribute('popover', 'manual'); panel.showPopover();
        positionCompactPreview(quickAdd);
      }
      trigger.setAttribute('aria-expanded', 'true');
    };

    const setLoading = (el, state) => {
      if (!el) return;
      el.classList.toggle('is-loading', state);
      if (state) {
        el.setAttribute('aria-busy', 'true');
      } else {
        el.removeAttribute('aria-busy');
      }
      el.disabled = state;
    };

    const setMessage = (quickAdd, message, type = '') => {
      const messageEl = quickAdd?.querySelector('[data-orjn-qa-message]');
      if (!messageEl) return;

      if (!message) {
        messageEl.hidden = true;
        messageEl.textContent = '';
        messageEl.removeAttribute('data-state');
        return;
      }

      messageEl.hidden = false;
      messageEl.textContent = message;
      if (type) {
        messageEl.setAttribute('data-state', type);
      } else {
        messageEl.removeAttribute('data-state');
      }
    };

    const getSectionIdsToRefresh = () => {
      const ids = new Set(['cart-icon-bubble', 'cart-live-region-text']);

      const collect = (el) => {
        if (!el) return;

        const explicitId = el.getAttribute('data-section-id');
        if (explicitId) ids.add(explicitId);

        const sectionWrapper = el.closest('[id^="shopify-section-"]');
        if (sectionWrapper) {
          ids.add(sectionWrapper.id.replace('shopify-section-', ''));
        }
      };

      drawerSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach(collect);
      });

      document.querySelectorAll('cart-items, .cart-items, [data-cart-items]').forEach(collect);
      document.querySelectorAll('.cart-count-bubble, [data-cart-count], [id*="cart-icon-bubble"]').forEach(collect);

      return Array.from(ids).filter(Boolean);
    };

    const patchSection = (sectionId, html) => {
      if (!sectionId || !html) return;

      const target =
        document.getElementById(`shopify-section-${sectionId}`) ||
        document.querySelector(`[data-section-id="${sectionId}"]`) ||
        document.querySelector(`#${CSS.escape(sectionId)}`);

      if (!target) return;

      if (target.id === `shopify-section-${sectionId}`) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;

        const incoming =
          wrapper.querySelector(`#shopify-section-${sectionId}`) ||
          wrapper.firstElementChild;

        if (incoming) {
          target.innerHTML = incoming.innerHTML;
        }
        return;
      }

      target.innerHTML = html;
    };

    const patchSections = (sections) => {
      if (!sections || typeof sections !== 'object') return;
      Object.entries(sections).forEach(([sectionId, html]) => {
        patchSection(sectionId, html);
      });
    };

    const getCart = async () => {
      const response = await fetch(`${baseUrl}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Unable to read cart.');
      return response.json();
    };

    const updateCountBubbles = (count) => {
      const value = String(count);

      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        const inner =
          bubble.querySelector('span') ||
          bubble.querySelector('.count') ||
          bubble;

        inner.textContent = value;
        bubble.classList.toggle('hidden', count < 1);
        bubble.classList.toggle('is-empty', count < 1);
      });

      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = value;
      });

      document.querySelectorAll('[id*="cart-icon-bubble"]').forEach((el) => {
        const inner =
          el.querySelector('.cart-count-bubble span') ||
          el.querySelector('.cart-count-bubble') ||
          el.querySelector('[data-cart-count]');

        if (inner) inner.textContent = value;
      });
    };

    const dispatchCartEvents = (cart) => {
      const detail = { cart, source: 'orjn-product-card' };

      [document, document.documentElement, window].forEach((target) => {
        target.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
        target.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
        target.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
        target.dispatchEvent(new CustomEvent('cart:open', { bubbles: true, detail }));
      });
    };

    const refreshThemeCartUI = async (sectionIds = []) => {
      if (!sectionIds.length) return;

      try {
        const response = await fetch(
          `${baseUrl}?sections=${encodeURIComponent(sectionIds.join(','))}`,
          {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            cache: 'no-store'
          }
        );

        if (!response.ok) return;

        const sections = await response.json();
        patchSections(sections);
      } catch (error) {}
    };

    const openCartDrawer = () => {
      drawerSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((drawer) => {
          drawer.classList.add('is-open', 'active');
          drawer.setAttribute('open', '');

          if (drawer.tagName === 'DETAILS') {
            drawer.open = true;
          }

          if (typeof drawer.show === 'function') {
            try {
              drawer.show();
            } catch (error) {}
          }
        });
      });
    };

    const addVariantAjax = async (variantId) => {
      const sectionIds = getSectionIdsToRefresh();

      const response = await fetch(`${baseUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1
            }
          ],
          sections: sectionIds.join(','),
          sections_url: `${window.location.pathname}${window.location.search}`
        })
      });

      if (!response.ok) {
        let errorMessage = 'Unable to add to cart.';

        try {
          const errorData = await response.json();
          errorMessage =
            errorData?.description ||
            errorData?.message ||
            errorMessage;
        } catch (error) {}

        throw new Error(errorMessage);
      }

      const data = await response.json();
      return {
        data,
        sectionIds
      };
    };

    const addVariantToCart = async (variantId, quickAdd, button) => {
      if (!variantId) return;

      setLoading(button, true);
      setMessage(quickAdd, '');

      try {
        const { data, sectionIds } = await addVariantAjax(variantId);

        if (data?.sections) {
          patchSections(data.sections);
        } else {
          await refreshThemeCartUI(sectionIds);
        }

        const cart = await getCart();

        updateCountBubbles(Number(cart?.item_count || 0));
        dispatchCartEvents(cart);
        openCartDrawer();
        placeQuickAdd(document);

        setMessage(quickAdd, 'Added to cart.', 'success');
        closePanel(quickAdd);
      } catch (error) {
        setMessage(
          quickAdd,
          error?.message || 'Unable to add to cart.',
          'error'
        );
      } finally {
        setLoading(button, false);
      }
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-orjn-qa-trigger]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();

        const quickAdd = trigger.closest('[data-orjn-qa]');
        if (!quickAdd) return;

        const willOpen = !quickAdd.classList.contains('is-open');

        if (willOpen) {
          openPanel(quickAdd);
        } else {
          closePanel(quickAdd);
        }
        return;
      }

      const sizeButton = event.target.closest('[data-orjn-qa-size]');
      if (sizeButton) {
        event.preventDefault();
        event.stopPropagation();

        const quickAdd = sizeButton.closest('[data-orjn-qa]');
        addVariantToCart(sizeButton.dataset.variantId, quickAdd, sizeButton);
        return;
      }

      const directButton = event.target.closest('[data-orjn-qa-direct]');
      if (directButton) {
        event.preventDefault();
        event.stopPropagation();

        const quickAdd = directButton.closest('[data-orjn-qa]');
        addVariantToCart(directButton.dataset.variantId, quickAdd, directButton);
        return;
      }

      if (!event.target.closest('[data-orjn-qa]')) {
        closeAllPanels();
      }
    });

    document.addEventListener('mouseover', (event) => {
      const quickAdd = event.target.closest('[data-orjn-qa]');
      if (!quickAdd) return;
      setCardActive(quickAdd, true);
    });

    document.addEventListener('mouseout', (event) => {
      const quickAdd = event.target.closest('[data-orjn-qa]');
      if (!quickAdd) return;

      if (!quickAdd.contains(event.relatedTarget) && !quickAdd.classList.contains('is-open')) {
        setCardActive(quickAdd, false);
      }
    });

    document.addEventListener('focusin', (event) => {
      const quickAdd = event.target.closest('[data-orjn-qa]');
      if (!quickAdd) return;
      setCardActive(quickAdd, true);
    });

    document.addEventListener('focusout', (event) => {
      const quickAdd = event.target.closest('[data-orjn-qa]');
      if (!quickAdd) return;

      requestAnimationFrame(() => {
        if (!quickAdd.contains(document.activeElement) && !quickAdd.classList.contains('is-open')) {
          setCardActive(quickAdd, false);
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeAllPanels();
      }
    });

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          placeQuickAdd(document);
        },
        { once: true }
      );
    } else {
      placeQuickAdd(document);
    }

    window.addEventListener('load', () => syncAllQuickAddOffsets(document), { once: true });
    window.addEventListener('resize', () => { syncAllQuickAddOffsets(document); closeAllPanels(); });
    window.addEventListener('scroll', (event) => { if (event.target?.closest?.('[data-orjn-qa-panel]')) return; if (window.innerWidth <= 749) closeAllPanels(); }, true);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          placeQuickAdd(node);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  })();
